import React, { useState, useRef, useCallback, useEffect } from "react";
import ReactDOM from "react-dom";
import { Form, Nav, Tab } from "react-bootstrap";
import MyIcon from "../Common/MyIcon";
import SolutionIcon from "../../assets/icons/Icon_Library/solutions icons/app_colouring/sol_resource_managment_fulfilment-01.svg2";
import SolutionFlowOverview from "../Solutions/SolutionFlowOverview";
import AiIcon from "../../assets/icons/Icon_Library/application icons/library/boomAI_icon.svg2";
import ProfileIcon from "../../assets/icons/Icon_Library/application icons/general/profile_fields.svg2";
import SolutionStep from "./SolutionStep";
import SolutionStepInputGroup from "./SolutionStepInputGroup";
import InputArgumentField from "../Builder/Components/sidebar/ProjectStartForm/InputArgumentField";
import SolutionImportInput from "../Solutions/SolutionImportInput";
import { parseSolutionData } from "../Solutions/services/jsonParsingService";
import { useGetContactSetsQuery } from "../store/slices/contactsSlice";
import { useGetReportingTopicsQuery } from "../store/slices/reportingSlice";
import {
  SolutionType,
  useGetApiDataCollectionsQuery,
  usePostAiPromptMutation,
  useUpdateApiConnectionMutation,
} from "../store/slices/settingsSlice";
import {
  initializeWeatherMonitor,
  initializePingMonitor,
  initializeInboundCampaign,
} from "../Solutions/services/deploymentService";
import MyCollapse from "../Common/MyCollapse";
import MyModal from "../Common/MyModal";
import labels from "../Builder/nodes/Logic/helpers/labels";
import { getDefaultBusinessHours } from "../Builder/Components/BusinessHoursForm";

interface SolutionSetupProps {
  solution?: SolutionType;
  setSolution?: React.Dispatch<React.SetStateAction<SolutionType>>;
  importedJSON?: any;
  setImportedJSON?: React.Dispatch<React.SetStateAction<any>>;
  manualFields?: string[];
  setManualFields?: React.Dispatch<React.SetStateAction<string[]>>;
  automaticallySelectedFields?: string[];
  setAutomaticallySelectedFields?: React.Dispatch<React.SetStateAction<string[]>>;
  initialVariableDefaults?: Record<string, any>;
  onChange?: (formData: Record<string, any>) => void;
  onMonitorsChange?: (monitors: Record<string, any>) => void;
  onInboundAPIsChange?: (inboundAPIs: Record<string, any>) => void;
  onInboundCampaignsChange?: (inboundCampaigns: Record<string, any>) => void;
  showDefaultValueInputsOnly?: boolean;
}

type SidebarDragArg = any;

interface SidebarDragState {
  arg: SidebarDragArg;
  top: number;
  left: number;
  width: number;
  height: number;
}

interface SidebarOverInputState {
  groupId: string;
  index: number;
  mode?: "insert" | "merge";
  targetIndex?: number;
  mergeCandidateIndex?: number;
}

interface NavDragGhostState {
  label: string;
  top: number;
  left: number;
  width: number;
  height: number;
}

interface FlowNodeSelection {
  nodeKind: "solution" | "step" | "group" | "input";
  action?: "edit";
  stepIndex?: number;
  groupId?: string;
  inputIndex?: number;
  inputFocusKey?: string;
  isFullscreen?: boolean;
}

interface PendingFlowSelection extends FlowNodeSelection {
  attempts: number;
}

interface DiagramInputModalState {
  stepIndex: number;
  groupId: string;
  inputIndex: number;
}

interface SolutionInputUsage {
  key: string;
  kind: "Condition" | "Action";
  label: string;
  logicNodeKey: string;
  componentLabel: string;
  workflowLabel?: string;
  eventLabel?: string;
  ruleLabel?: string;
}

interface SetupAiSuggestion {
  title: string;
  detail: string;
  stage?: string;
  action?: {
    change?: {
      target: "step" | "group" | "input";
      stepIndex?: number;
      groupId?: string;
      inputIndex?: number;
      field: "name" | "description" | "label";
      from?: string;
      to: string;
    };
    changes?: Array<{
      target: "step" | "group" | "input";
      stepIndex?: number;
      groupId?: string;
      inputIndex?: number;
      field: "name" | "description" | "label";
      from?: string;
      to: string;
    }>;
    steps?: Array<{
      stepIndex?: number;
      name?: string;
      description?: string;
      groupId?: string;
      groupIds?: string[];
      inputGroupIds?: string[];
    }>;
  };
}

interface SetupAiReviewStage {
  key: string;
  label: string;
  focus: string;
  maxSuggestions: number;
}

interface SetupAiChangedField {
  target: "step" | "group" | "input";
  field: "name" | "description" | "label";
  stepIndex: number;
  groupId?: string;
  inputIndex?: number;
  context: string;
  from: string;
  to: string;
  changeType?: "added" | "moved" | "label-edit";
}

const SETUP_AI_MAX_SUGGESTIONS = 10;
const SETUP_AI_CHANGE_DEBOUNCE_MS = 1500;

const SETUP_AI_CHANGE_STAGE: SetupAiReviewStage = {
  key: "change-text-review",
  label: "Change review",
  focus:
    "Check only the changed setup text for spelling, grammar, clarity, and concise wording.",
  maxSuggestions: 3,
};

const SETUP_AI_FLOW_STAGE: SetupAiReviewStage = {
  key: "flow-grouping",
  label: "Flow and grouping",
  focus:
    "Check only whether existing input groups should be reordered, moved, split into clearer setup steps, or combined into a more natural setup flow.",
  maxSuggestions: 7,
};

const SETUP_AI_COMPLETION_STAGE: SetupAiReviewStage = {
  key: "setup-completion",
  label: "Setup completion",
  focus:
    "Check whether the setup is missing useful steps, input groups, required configuration fields, defaults, descriptions, or clearer guidance needed before a user can complete it confidently.",
  maxSuggestions: 7,
};

const stringifySetupValue = (value: any): string => {
  if (value === null || value === undefined || value === "") return "";
  if (Array.isArray(value)) {
    return value.map(stringifySetupValue).filter(Boolean).join(", ");
  }
  if (typeof value === "object") {
    return stringifySetupValue(
      value.Label ||
        value.label ||
        value.Name ||
        value.name ||
        value.Value ||
        value.value ||
        value.UUID ||
        value.Uuid
    );
  }

  return String(value);
};

const getSetupHumanLabel = (value: any) => {
  const rawValue = stringifySetupValue(value);
  return rawValue
    ? (labels as Record<string, string>)[rawValue] || rawValue
    : "";
};

const normalizeSetupTokens = (tokens: any[]) => {
  return Array.from(
    new Set(tokens.filter(Boolean).map((token) => String(token).toLowerCase()))
  );
};

const getSetupComponentUuid = (value: any) => {
  return stringifySetupValue(
    value?.ComponentUuid || value?.SolutionComponentUuid || value?.componentUuid
  ).toLowerCase();
};

const getSetupReferenceTokens = (ref: any) => {
  const componentUuid = getSetupComponentUuid(ref);
  const field =
    ref?.Field || ref?.field || ref?.fieldKey || ref?.Uuid || ref?.UUID;

  return [
    ref?.UUID,
    ref?.Uuid,
    ref?.Value,
    ref?.Field,
    ref?.field,
    ref?.fieldKey,
    ref?.Name,
    componentUuid && field ? `${componentUuid}.${field}` : null,
    componentUuid && field ? `${componentUuid}:${field}` : null,
  ];
};

const getSetupInputTokens = (input: any) => {
  const mergedRefTokens = (input?.MergedInputRefs || []).flatMap(
    getSetupReferenceTokens
  );

  return normalizeSetupTokens([
    ...getSetupReferenceTokens(input),
    ...mergedRefTokens,
  ]);
};

const getSetupScopedReferenceTokens = (ref: any) => {
  const componentUuid = getSetupComponentUuid(ref);
  const field =
    ref?.Field || ref?.field || ref?.fieldKey || ref?.Uuid || ref?.UUID;

  return [
    ref?.UUID,
    componentUuid && field ? `${componentUuid}.${field}` : null,
    componentUuid && field ? `${componentUuid}:${field}` : null,
  ];
};

const getSetupInputLookupTokens = (input: any) => {
  const hasComponentIdentity = Boolean(
    getSetupComponentUuid(input) ||
      input?.MergedInputRefs?.some((ref: any) => getSetupComponentUuid(ref))
  );

  if (!hasComponentIdentity) {
    return getSetupInputTokens(input);
  }

  const mergedRefTokens = (input?.MergedInputRefs || []).flatMap(
    getSetupScopedReferenceTokens
  );

  return normalizeSetupTokens([
    ...getSetupScopedReferenceTokens(input),
    ...mergedRefTokens,
  ]);
};

const getSetupInputArgumentTokens = (argument: any, componentUuid?: string) => {
  return normalizeSetupTokens([
    argument?.UUID,
    argument?.Uuid,
    argument?.Value,
    argument?.Field,
    argument?.field,
    argument?.fieldKey,
    argument?.Name,
    componentUuid && argument?.Field
      ? `${componentUuid}.${argument.Field}`
      : null,
    componentUuid && argument?.Field
      ? `${componentUuid}:${argument.Field}`
      : null,
    componentUuid && argument?.UUID
      ? `${componentUuid}.${argument.UUID}`
      : null,
    componentUuid && argument?.UUID
      ? `${componentUuid}:${argument.UUID}`
      : null,
    componentUuid && argument?.Uuid
      ? `${componentUuid}.${argument.Uuid}`
      : null,
    componentUuid && argument?.Uuid
      ? `${componentUuid}:${argument.Uuid}`
      : null,
  ]);
};

const getSetupInputComponentUuids = (input: any) => {
  return normalizeSetupTokens([
    getSetupComponentUuid(input),
    ...(input?.MergedInputRefs || []).map(getSetupComponentUuid),
  ]);
};

const getSetupFirstOrderedArray = (...candidates: any[]) => {
  const match = candidates.find((candidate) =>
    Array.isArray(candidate) ? candidate.length > 0 : Boolean(candidate)
  );

  return getSetupOrderedArray(match || []);
};

const getSetupOrderValue = (item: any, fallbackIndex = 0) => {
  const order =
    item?.Order ??
    item?.order ??
    item?.SortOrder ??
    item?.sortOrder ??
    item?.Position ??
    item?.position;

  return Number.isFinite(Number(order)) ? Number(order) : fallbackIndex;
};

const getSetupOrderedArray = (items: any) => {
  return Array.isArray(items)
    ? [...items]
        .map((item, index) => ({ item, index }))
        .sort(
          (firstItem, secondItem) =>
            getSetupOrderValue(firstItem.item, firstItem.index) -
              getSetupOrderValue(secondItem.item, secondItem.index) ||
            firstItem.index - secondItem.index
        )
        .map(({ item }) => item)
    : [];
};

const getSetupCurrentStepsSummary = (solutionValue: SolutionType | undefined) => {
  return (solutionValue?.ComponentConfig?.steps || []).map(
    (step: any, stepIndex: number) => ({
      order: stepIndex + 1,
      stepIndex,
      name: step?.name,
      description: step?.description,
      groups: (step?.inputGroups || []).map((group: any, groupIndex: number) =>
        ({
          order: groupIndex + 1,
          id: group?.id,
          name: group?.name,
          description: group?.description,
          inputs: (group?.inputs || []).map((input: any, inputIndex: number) =>
            ({
              order: inputIndex + 1,
              inputIndex,
              uuid: input?.UUID || input?.Uuid,
              field: input?.Field,
              label: input?.Label || input?.Field || input?.Name,
              description: input?.Description,
              format: input?.Format,
              required: input?.Required || input?.IsRequired,
              defaultValue: input?.DefaultValue || input?.Default,
              mergedInputCount: input?.MergedInputRefs?.length || 0,
            })
          ),
        })
      ),
    })
  );
};

const getSetupCompletionAvailableGroups = (solutionValue: SolutionType | undefined) => {
  return (solutionValue?.Components || []).flatMap(
    (component: any, componentIndex: number) => {
      const componentUuid = stringifySetupValue(component?.ComponentUuid);
      const componentLabel =
        stringifySetupValue(
          component?.Item?.Label || component?.Label || component?.Role
        ) || `Component ${componentIndex + 1}`;
      const componentDescription = stringifySetupValue(
        component?.Item?.Description || component?.Description
      );
      const parameterArgs = getSetupOrderedArray(
        component?.Item?.InputArguments || []
      ).filter(
        (arg: any) => arg?.IsParameter && !arg?.SolutionDoNotConfigure
      );
      const groupedArgs = parameterArgs.reduce(
        (acc: Record<string, any[]>, arg: any) => {
          const groupUuid = stringifySetupValue(arg?.GroupUuid);
          acc[groupUuid] = [...(acc[groupUuid] || []), arg];
          return acc;
        },
        {}
      );
      const variableGroups = getSetupOrderedArray(
        component?.Item?.VariableGroups || []
      );
      const groups = variableGroups
        .map((group: any, groupIndex: number) => {
          const groupUuid = stringifySetupValue(group?.GroupUuid || groupIndex);
          const inputs = getSetupOrderedArray(groupedArgs[groupUuid] || []);

          return {
            id: `component-step:${componentUuid}:${component?.Role}:${groupUuid}`,
            source: "component-inputs",
            componentUuid,
            componentRole: component?.Role,
            componentLabel,
            name: stringifySetupValue(group?.Label) || "Input Group",
            description:
              stringifySetupValue(group?.Description) || componentDescription,
            inputs: inputs.map((input: any, inputIndex: number) => ({
              uuid: `component-input:${componentUuid}:${component?.Role}:${
                input?.Field || input?.Label || input?.UUID || inputIndex
              }`,
              field: input?.Field,
              label: input?.Label || input?.Field || input?.Name,
              description: input?.Description,
              format: input?.Format,
              required: input?.Required || input?.IsRequired,
              defaultValue: input?.DefaultValue || input?.Default,
            })),
          };
        })
        .filter((group: any) => group.inputs.length > 0);
      const defaultInputs = getSetupOrderedArray(groupedArgs[""] || []);

      if (variableGroups.length === 0 && defaultInputs.length > 0) {
        groups.push({
          id: `component-step:${componentUuid}:${component?.Role}:default`,
          source: "component-inputs",
          componentUuid,
          componentRole: component?.Role,
          componentLabel,
          name: "Default Group",
          description: componentDescription,
          inputs: defaultInputs.map((input: any, inputIndex: number) => ({
            uuid: `component-input:${componentUuid}:${component?.Role}:${
              input?.Field || input?.Label || input?.UUID || inputIndex
            }`,
            field: input?.Field,
            label: input?.Label || input?.Field || input?.Name,
            description: input?.Description,
            format: input?.Format,
            required: input?.Required || input?.IsRequired,
            defaultValue: input?.DefaultValue || input?.Default,
          })),
        });
      }

      return groups;
    }
  );
};

const getSetupCompletionComponentsSummary = (
  solutionValue: SolutionType | undefined
) => {
  return (solutionValue?.Components || []).map(
    (component: any, componentIndex: number) => ({
      order: componentIndex + 1,
      componentUuid: component?.ComponentUuid,
      role: component?.Role,
      label:
        component?.Item?.Label ||
        component?.Label ||
        component?.Role ||
        `Component ${componentIndex + 1}`,
      description: component?.Item?.Description || component?.Description,
      variableGroups: getSetupOrderedArray(
        component?.Item?.VariableGroups || []
      ).map((group: any, groupIndex: number) => ({
        order: groupIndex + 1,
        groupUuid: group?.GroupUuid,
        label: group?.Label,
        description: group?.Description,
      })),
      inputArguments: getSetupOrderedArray(
        component?.Item?.InputArguments || []
      ).map((argument: any, argumentIndex: number) => ({
        order: argumentIndex + 1,
        field: argument?.Field,
        label: argument?.Label || argument?.Field || argument?.Name,
        description: argument?.Description,
        groupUuid: argument?.GroupUuid,
        format: argument?.Format,
        required: argument?.Required || argument?.IsRequired,
        defaultValue: argument?.DefaultValue || argument?.Default,
        configurable:
          Boolean(argument?.IsParameter) && !argument?.SolutionDoNotConfigure,
      })),
    })
  );
};

const getSetupCompletionGroupTemplates = (solutionValue: SolutionType | undefined) => {
  return (solutionValue?.Components || []).flatMap(
    (component: any, componentIndex: number) => {
      const componentUuid = stringifySetupValue(component?.ComponentUuid);
      const componentLabel =
        stringifySetupValue(
          component?.Item?.Label || component?.Label || component?.Role
        ) || `Component ${componentIndex + 1}`;
      const componentDescription = stringifySetupValue(
        component?.Item?.Description || component?.Description
      );
      const parameterArgs = getSetupOrderedArray(
        component?.Item?.InputArguments || []
      )
        .filter(
          (arg: any) => arg?.IsParameter && !arg?.SolutionDoNotConfigure
        )
        .map((arg: any, inputIndex: number) => ({
          ...arg,
          ComponentUuid: componentUuid,
          UUID: `component-input:${componentUuid}:${component?.Role}:${
            arg?.Field || arg?.Label || arg?.UUID || inputIndex
          }`,
          Label: arg?.Label || arg?.Field || arg?.Name || `Input ${inputIndex + 1}`,
          Description: arg?.Description || "",
        }));
      const groupedArgs = parameterArgs.reduce(
        (acc: Record<string, any[]>, arg: any) => {
          const groupUuid = stringifySetupValue(arg?.GroupUuid);
          acc[groupUuid] = [...(acc[groupUuid] || []), arg];
          return acc;
        },
        {}
      );
      const variableGroups = getSetupOrderedArray(
        component?.Item?.VariableGroups || []
      );
      const groups = variableGroups
        .map((group: any, groupIndex: number) => {
          const groupUuid = stringifySetupValue(group?.GroupUuid || groupIndex);
          const inputs = getSetupOrderedArray(groupedArgs[groupUuid] || []);

          return {
            id: `component-step:${componentUuid}:${component?.Role}:${groupUuid}`,
            name:
              group?.Label && group?.Label !== "Default Group"
                ? `${componentLabel}: ${group.Label}`
                : componentLabel,
            description:
              stringifySetupValue(group?.Description) ||
              componentDescription ||
              "Configure component inputs.",
            inputs,
          };
        })
        .filter((group: any) => group.inputs.length > 0);
      const defaultInputs = getSetupOrderedArray(groupedArgs[""] || []);

      if (variableGroups.length === 0 && defaultInputs.length > 0) {
        groups.push({
          id: `component-step:${componentUuid}:${component?.Role}:default`,
          name: componentLabel,
          description: componentDescription || "Configure component inputs.",
          inputs: defaultInputs,
        });
      }

      return groups;
    }
  );
};

const applySetupCompletionSteps = (
  currentSteps: any[],
  actionSteps: NonNullable<SetupAiSuggestion["action"]>["steps"] = [],
  availableGroupTemplates: any[] = []
) => {
  if (!actionSteps.length) return { steps: currentSteps, applied: false };

  const templateByGroupId = new Map(
    availableGroupTemplates.map((group: any) => [group.id, group])
  );
  const groupById = new Map<string, any>();

  currentSteps.forEach((step: any) => {
    (step?.inputGroups || []).forEach((group: any) => {
      if (group?.id) groupById.set(group.id, group);
    });
  });

  const selectedGroupIds = new Set<string>();
  const nextSteps = actionSteps
    .map((actionStep: any, stepIndex: number) => {
      const groupIds = Array.from(
        new Set(
          [
            actionStep?.groupId,
            ...(actionStep?.groupIds || []),
            ...(actionStep?.inputGroupIds || []),
          ]
            .map(stringifySetupValue)
            .filter(Boolean)
        )
      );
      const inputGroups = groupIds
        .map((groupId) => {
          selectedGroupIds.add(groupId);
          const existingGroup = groupById.get(groupId);
          const templateGroup = templateByGroupId.get(groupId);
          const group = existingGroup || templateGroup;
          if (!group) return null;

          return {
            ...group,
            inputs: [...(group.inputs || [])],
          };
        })
        .filter(Boolean);

      if (!inputGroups.length) return null;

      return {
        name:
          stringifySetupValue(actionStep?.name) ||
          `Step ${Number(actionStep?.stepIndex ?? stepIndex) + 1}`,
        description: stringifySetupValue(actionStep?.description),
        inputGroups,
      };
    })
    .filter(Boolean);

  if (!nextSteps.length) return { steps: currentSteps, applied: false };

  const untouchedSteps = currentSteps
    .map((step: any) => ({
      ...step,
      inputGroups: (step?.inputGroups || []).filter(
        (group: any) => !selectedGroupIds.has(group?.id)
      ),
    }))
    .filter((step: any) => (step?.inputGroups || []).length > 0);

  return {
    steps: [...nextSteps, ...untouchedSteps],
    applied: true,
  };
};

const applySetupTextChanges = (
  currentSteps: any[],
  changes: NonNullable<SetupAiSuggestion["action"]>["changes"] = []
) => {
  if (!changes.length) return { steps: currentSteps, applied: false };

  let applied = false;
  const fieldNameByTarget = {
    step: {
      name: "name",
      description: "description",
      label: "name",
    },
    group: {
      name: "name",
      description: "description",
      label: "name",
    },
    input: {
      name: "Name",
      description: "Description",
      label: "Label",
    },
  } as const;
  const nextSteps = currentSteps.map((step: any, stepIndex: number) => {
    const stepChanges = changes.filter(
      (change) => Number(change.stepIndex) === stepIndex && change.to
    );
    if (stepChanges.length === 0) return step;

    let nextStep = step;

    stepChanges.forEach((change) => {
      const fieldName = fieldNameByTarget[change.target]?.[change.field];
      if (!fieldName) return;

      if (change.target === "step") {
        nextStep = {
          ...nextStep,
          [fieldName]: change.to,
        };
        applied = true;
        return;
      }

      if (change.target === "group") {
        let groupMatched = false;
        const inputGroups = (nextStep.inputGroups || []).map((group: any) => {
          if (group?.id !== change.groupId) return group;
          groupMatched = true;
          return {
            ...group,
            [fieldName]: change.to,
          };
        });

        if (groupMatched) {
          nextStep = {
            ...nextStep,
            inputGroups,
          };
          applied = true;
        }
        return;
      }

      const inputIndex = Number(change.inputIndex);
      if (!Number.isInteger(inputIndex)) return;

      let inputMatched = false;
      const inputGroups = (nextStep.inputGroups || []).map((group: any) => {
        if (group?.id !== change.groupId) return group;

        return {
          ...group,
          inputs: (group.inputs || []).map((input: any, index: number) => {
            if (index !== inputIndex) return input;
            inputMatched = true;
            return {
              ...input,
              [fieldName]: change.to,
            };
          }),
        };
      });

      if (inputMatched) {
        nextStep = {
          ...nextStep,
          inputGroups,
        };
        applied = true;
      }
    });

    return nextStep;
  });

  return { steps: nextSteps, applied };
};

const parseSetupAiSuggestions = (
  answer: any,
  stage?: SetupAiReviewStage
): SetupAiSuggestion[] => {
  const normalizeSuggestion = (item: any, index: number) => {
    if (typeof item === "string") {
      return { title: `Suggestion ${index + 1}`, detail: item };
    }

    if (!item || typeof item !== "object") return null;

    const title = stringifySetupValue(
      item.title || item.Title || item.summary || item.Summary || item.name
    );
    const detail = stringifySetupValue(
      item.detail ||
        item.Detail ||
        item.reason ||
        item.Reason ||
        item.description ||
        item.Description ||
        item.suggestion ||
        item.Suggestion ||
        item.improvement ||
        item.Improvement
    );
    const action =
      item.action ||
      item.Action ||
      item.apply ||
      item.Apply ||
      item.proposedLayout ||
      item.ProposedLayout;
    const change = action?.change || action?.Change;
    const changes = action?.changes || action?.Changes;
    const steps = action?.steps || action?.Steps;
    const changeFrom = stringifySetupValue(change?.from || change?.From);
    const changeTo = stringifySetupValue(change?.to || change?.To);
    const displayTitle =
      changeFrom && changeTo ? `${changeFrom} => ${changeTo}` : title;
    const normalizedAction =
      action && typeof action === "object"
        ? {
            ...action,
            change,
            changes,
            steps,
          }
        : undefined;

    if (!title && !detail) return null;

    return {
      title: displayTitle || `Suggestion ${index + 1}`,
      detail,
      stage: stage?.label,
      action: normalizedAction,
    };
  };

  const normalizeList = (value: any) => {
    if (value?.steps || value?.Steps) {
      return [
        {
          title: "Complete setup",
          detail: "Apply completed setup structure.",
          stage: stage?.label,
          action: { steps: value.steps || value.Steps },
        },
      ];
    }

    const list = Array.isArray(value)
      ? value
      : value?.suggestions ||
        value?.Suggestions ||
        value?.improvements ||
        value?.Improvements ||
        [];

    return list
      .map(normalizeSuggestion)
      .filter(Boolean) as SetupAiSuggestion[];
  };

  if (!answer) return [];
  if (typeof answer === "object") return normalizeList(answer);

  const cleanAnswer = String(answer)
    .replace(/^```json\s*/i, "")
    .replace(/^```\s*/i, "")
    .replace(/```$/i, "")
    .trim();

  try {
    return normalizeList(JSON.parse(cleanAnswer));
  } catch (error) {
    return cleanAnswer
      .split(/\n+/)
      .map((line) => line.replace(/^[-*\d.)\s]+/, "").trim())
      .filter(Boolean)
      .map((detail, index) => ({
        title: `Suggestion ${index + 1}`,
        detail,
        stage: stage?.label,
      }));
  }
};

const addSetupChangedField = (
  changes: SetupAiChangedField[],
  target: SetupAiChangedField["target"],
  field: SetupAiChangedField["field"],
  previousValue: any,
  nextValue: any,
  stepIndex: number,
  context: string,
  groupId?: string,
  inputIndex?: number
) => {
  const from = stringifySetupValue(previousValue);
  const to = stringifySetupValue(nextValue);
  if (from === to) return;

  changes.push({
    target,
    field,
    stepIndex,
    groupId,
    inputIndex,
    context,
    from,
    to,
  });
};

const getSetupInputChangeKey = (input: any, fallbackIndex: number) => {
  return stringifySetupValue(
    input?.UUID ||
      input?.Uuid ||
      input?.Field ||
      input?.Name ||
      input?.Label ||
      input?.MergedInputRefs?.map(getSetupReferenceTokens).flat().join(":") ||
      fallbackIndex
  );
};

const getSetupGroupChangeKey = (group: any, fallbackIndex: number) => {
  return stringifySetupValue(group?.id || group?.UUID || group?.Uuid || fallbackIndex);
};

const createSetupNameReviewChange = ({
  target,
  field,
  stepIndex,
  groupId,
  inputIndex,
  context,
  to,
  changeType,
}: {
  target: SetupAiChangedField["target"];
  field: SetupAiChangedField["field"];
  stepIndex: number;
  groupId?: string;
  inputIndex?: number;
  context: string;
  to: any;
  changeType: SetupAiChangedField["changeType"];
}): SetupAiChangedField | null => {
  const value = stringifySetupValue(to);
  if (!value.trim()) return null;

  return {
    target,
    field,
    stepIndex,
    groupId,
    inputIndex,
    context,
    from: "",
    to: value,
    changeType,
  };
};

const getSetupNameReviewChanges = ({
  step,
  stepIndex,
  group,
  groupId,
  input,
  inputIndex,
  changeType,
}: {
  step: any;
  stepIndex: number;
  group?: any;
  groupId?: string;
  input?: any;
  inputIndex?: number;
  changeType: SetupAiChangedField["changeType"];
}) => {
  const stepName = stringifySetupValue(step?.name || `Step ${stepIndex + 1}`);
  const groupName = stringifySetupValue(group?.name || groupId || stepName);
  const inputLabel = stringifySetupValue(input?.Label || input?.Field || input?.Name);
  const context = [stepName, groupName, inputLabel]
    .filter(Boolean)
    .join(" > ");

  return [
    createSetupNameReviewChange({
      target: "step",
      field: "name",
      stepIndex,
      context: context || `Step ${stepIndex + 1}`,
      to: step?.name,
      changeType,
    }),
    createSetupNameReviewChange({
      target: "step",
      field: "description",
      stepIndex,
      context: context || `Step ${stepIndex + 1}`,
      to: step?.description,
      changeType,
    }),
    group &&
      createSetupNameReviewChange({
        target: "group",
        field: "name",
        stepIndex,
        groupId,
        context: context || groupName || stepName,
        to: group?.name,
        changeType,
      }),
    group &&
      createSetupNameReviewChange({
        target: "group",
        field: "description",
        stepIndex,
        groupId,
        context: context || groupName || stepName,
        to: group?.description,
        changeType,
      }),
    input &&
      createSetupNameReviewChange({
        target: "input",
        field: "label",
        stepIndex,
        groupId,
        inputIndex,
        context: context || inputLabel || groupName || stepName,
        to: input?.Label || input?.Field || input?.Name,
        changeType,
      }),
    input &&
      createSetupNameReviewChange({
        target: "input",
        field: "description",
        stepIndex,
        groupId,
        inputIndex,
        context: context || inputLabel || groupName || stepName,
        to: input?.Description,
        changeType,
      }),
  ].filter(Boolean) as SetupAiChangedField[];
};

const getSetupChangedFields = (
  previousSteps: any[],
  nextSteps: any[]
): SetupAiChangedField[] => {
  const changes: SetupAiChangedField[] = [];
  const previousInputLocationsByKey = new Map<string, string>();
  const previousGroupLocationsByKey = new Map<string, string>();

  previousSteps.forEach((previousStep: any, previousStepIndex: number) => {
    (previousStep?.inputGroups || []).forEach((previousGroup: any, previousGroupIndex: number) => {
      const previousGroupKey = getSetupGroupChangeKey(previousGroup, previousGroupIndex);
      if (previousGroupKey) {
        previousGroupLocationsByKey.set(previousGroupKey, `${previousStepIndex}`);
      }

      (previousGroup?.inputs || []).forEach((previousInput: any, previousInputIndex: number) => {
        const previousInputKey = getSetupInputChangeKey(previousInput, previousInputIndex);
        if (previousInputKey) {
          previousInputLocationsByKey.set(
            previousInputKey,
            `${previousStepIndex}:${stringifySetupValue(previousGroup?.id)}`
          );
        }
      });
    });
  });

  nextSteps.forEach((nextStep: any, stepIndex: number) => {
    const previousStep = previousSteps[stepIndex] || {};
    const stepName = stringifySetupValue(nextStep?.name || `Step ${stepIndex + 1}`);

    addSetupChangedField(
      changes,
      "step",
      "name",
      previousStep?.name,
      nextStep?.name,
      stepIndex,
      `Step ${stepIndex + 1}`
    );
    addSetupChangedField(
      changes,
      "step",
      "description",
      previousStep?.description,
      nextStep?.description,
      stepIndex,
      stepName || `Step ${stepIndex + 1}`
    );

    const previousGroupsById = new Map<string, any>();
    (previousStep?.inputGroups || []).forEach((group: any, groupIndex: number) => {
      const groupKey = getSetupGroupChangeKey(group, groupIndex);
      if (groupKey) previousGroupsById.set(groupKey, group);
    });

    (nextStep?.inputGroups || []).forEach((nextGroup: any, groupIndex: number) => {
      const groupId = stringifySetupValue(nextGroup?.id);
      const groupKey = getSetupGroupChangeKey(nextGroup, groupIndex);
      const previousGroup = groupKey ? previousGroupsById.get(groupKey) || {} : {};
      const groupName = stringifySetupValue(nextGroup?.name || groupId || stepName);

      if (groupKey) {
        const previousGroupLocation = previousGroupLocationsByKey.get(groupKey);
        const nextGroupLocation = `${stepIndex}`;

        if (!previousGroupLocation || previousGroupLocation !== nextGroupLocation) {
          changes.push(
            ...getSetupNameReviewChanges({
              step: nextStep,
              stepIndex,
              group: nextGroup,
              groupId,
              changeType: previousGroupLocation ? "moved" : "added",
            })
          );
        }
      }

      addSetupChangedField(
        changes,
        "group",
        "name",
        previousGroup?.name,
        nextGroup?.name,
        stepIndex,
        `${stepName || `Step ${stepIndex + 1}`} group`,
        groupId
      );
      addSetupChangedField(
        changes,
        "group",
        "description",
        previousGroup?.description,
        nextGroup?.description,
        stepIndex,
        groupName || stepName || `Step ${stepIndex + 1}`,
        groupId
      );

      const previousInputs = previousGroup?.inputs || [];
      const previousInputsByKey = new Map<string, any>();
      previousInputs.forEach((input: any, inputIndex: number) => {
        previousInputsByKey.set(getSetupInputChangeKey(input, inputIndex), input);
      });

      (nextGroup?.inputs || []).forEach((nextInput: any, inputIndex: number) => {
        const inputKey = getSetupInputChangeKey(nextInput, inputIndex);
        const previousInput = previousInputsByKey.get(inputKey);
        const inputContext = stringifySetupValue(
          nextInput?.Label || nextInput?.Field || nextInput?.Name || groupName
        );

        if (inputKey) {
          const previousInputLocation = previousInputLocationsByKey.get(inputKey);
          const nextInputLocation = `${stepIndex}:${groupId}`;

          if (!previousInputLocation || previousInputLocation !== nextInputLocation) {
            changes.push(
              ...getSetupNameReviewChanges({
                step: nextStep,
                stepIndex,
                group: nextGroup,
                groupId,
                input: nextInput,
                inputIndex,
                changeType: previousInputLocation ? "moved" : "added",
              })
            );
          }
        }

        if (!previousInput) {
          return;
        }

        addSetupChangedField(
          changes,
          "input",
          "label",
          previousInput?.Label || previousInput?.Field || previousInput?.Name,
          nextInput?.Label || nextInput?.Field || nextInput?.Name,
          stepIndex,
          inputContext || groupName || stepName,
          groupId,
          inputIndex
        );
        addSetupChangedField(
          changes,
          "input",
          "description",
          previousInput?.Description,
          nextInput?.Description,
          stepIndex,
          inputContext || groupName || stepName,
          groupId,
          inputIndex
        );
      });
    });
  });

  const meaningfulChanges = changes.filter(({ to }) => to.trim().length > 0);
  const latestChange = meaningfulChanges[meaningfulChanges.length - 1];

  if (
    latestChange?.changeType === "added" ||
    latestChange?.changeType === "moved"
  ) {
    return meaningfulChanges.filter(
      (change) => {
        if (
          change.changeType !== latestChange.changeType ||
          change.stepIndex !== latestChange.stepIndex ||
          (change.field !== "name" &&
            change.field !== "label" &&
            change.field !== "description")
        ) {
          return false;
        }

        if (change.target === "step") return true;
        if (change.target === "group") return change.groupId === latestChange.groupId;
        return (
          change.groupId === latestChange.groupId &&
          change.inputIndex === latestChange.inputIndex
        );
      }
    );
  }

  if (latestChange?.target === "input" && latestChange.field === "label") {
    return [{ ...latestChange, changeType: "label-edit" }];
  }

  if (
    latestChange &&
    (latestChange.field === "name" || latestChange.field === "description")
  ) {
    return [latestChange];
  }

  return [];
};

const buildSetupChangeAiPrompt = (changes: SetupAiChangedField[]) => {
  const isItemNameReview = changes.some(
    (change) => change.changeType === "added" || change.changeType === "moved"
  );
  const isNameOrDescriptionReview = changes.some(
    (change) => change.field === "name" || change.field === "description"
  );
  const reviewSubject = isItemNameReview
    ? "these setup names and descriptions for the added or moved item"
    : isNameOrDescriptionReview
    ? "this changed setup name or description"
    : "this changed input label";
  const contextLabel = isItemNameReview
    ? "Added or moved item naming and description context"
    : isNameOrDescriptionReview
    ? "Changed setup name or description"
    : "Changed input label";
  const reviewInstruction = isItemNameReview
    ? "Check whether the step name and description, input group name and description, and input label and description are appropriate, clear, and specific for where the item now sits. Suggest better wording only for the provided fields."
    : isNameOrDescriptionReview
    ? "Only suggest spelling, grammar, clarity, or concise wording changes for the provided setup name or description."
    : "Only suggest spelling, grammar, clarity, or concise wording changes for this exact input label.";

  return `Review only ${reviewSubject}.
Return JSON only in this shape: {"suggestions":[{"title":"old text => improved text","detail":"why this change helps","action":{"change":{"target":"step|group|input","stepIndex":0,"groupId":"existing group id when present","inputIndex":0,"field":"name|description|label","from":"changed text","to":"improved text"}}}]}.
Return no more than ${SETUP_AI_CHANGE_STAGE.maxSuggestions} suggestions.
${reviewInstruction}
Do not suggest setup flow, grouping, ordering, or changes to unrelated fields.
If the text is already good, return {"suggestions":[]}.

${contextLabel}:
${JSON.stringify(isItemNameReview ? { changes } : { change: changes[0] }, null, 2)}`;
};

const buildSetupAiPrompt = (
  solutionValue: SolutionType | undefined,
  stage: SetupAiReviewStage
) => {
  const steps = getSetupCurrentStepsSummary(solutionValue);

  return `Review this solution setup structure for this stage: ${stage.label}.
Focus: ${stage.focus}
Return JSON only in this shape: {"suggestions":[{"title":"short title","detail":"specific improvement","action":{"steps":[{"name":"step name","description":"step description","groupIds":["existing group id"]}]}}]}.
Return no more than ${stage.maxSuggestions} suggestions for this stage.
Do not suggest spelling, grammar, or wording changes. Only suggest flow/grouping/order improvements using existing group IDs.
If there is no useful flow/grouping change, return {"suggestions":[]}.
Keep suggestions practical for a user configuring a solution.

Current setup:
${JSON.stringify({ steps }, null, 2)}`;
};

const buildSetupCompletionAiPrompt = (
  solutionValue: SolutionType | undefined,
  stage: SetupAiReviewStage
) => {
  const steps = getSetupCurrentStepsSummary(solutionValue);
  const components = getSetupCompletionComponentsSummary(solutionValue);
  const availableGroups = getSetupCompletionAvailableGroups(solutionValue);

  return `Complete this solution setup using the current setup state and the solution components.
Focus: ${stage.focus}
Return JSON only in this shape: {"suggestions":[{"title":"Complete setup","detail":"what was completed","action":{"steps":[{"name":"step name","description":"step description","groupIds":["available or current group id"]}],"changes":[{"target":"step|group|input","stepIndex":0,"groupId":"existing group id when present","inputIndex":0,"field":"name|description|label","from":"current text","to":"completed text"}]}}]}.
Return no more than ${stage.maxSuggestions} suggestions.
Use action.steps to return the completed setup structure. Include all groups that a user should configure before using the solution. Use only group IDs from current setup or available component groups. You may create new steps by grouping available component groups into a natural setup flow.
Use action.changes only for text improvements to existing step, group, input label, or description fields.
Do not return suggestions that require new fields, custom code, or unavailable group IDs.
If the setup looks complete, return {"suggestions":[]}.

Current setup:
${JSON.stringify({ steps }, null, 2)}

Solution components:
${JSON.stringify({ components }, null, 2)}

Available setup groups that can be placed in action.steps:
${JSON.stringify({ availableGroups }, null, 2)}`;
};

const getSetupConditionTypeKey = (condition: any) => {
  return stringifySetupValue(
    condition?.ConditionType ||
      condition?.Type ||
      condition?.Condition ||
      condition?.Field ||
      condition?.Kind ||
      condition?.Label
  );
};

const getSetupActionTypeKey = (action: any) => {
  return stringifySetupValue(
    action?.ActionType ||
      action?.Type ||
      action?.ActionKind ||
      action?.Kind ||
      action?.SolutionRole ||
      action?.Label
  );
};

const getSetupLogicValueStrings = (source: any) => {
  const values = new Set<string>();

  const collectReferenceTokens = (value: any) => {
    getSetupReferenceTokens(value).forEach((token) => {
      if (token) values.add(String(token).toLowerCase());
    });
  };

  const isLogicOptionRecord = (value: any) => {
    return (
      value &&
      typeof value === "object" &&
      !Array.isArray(value) &&
      (Object.prototype.hasOwnProperty.call(value, "Type") ||
        Object.prototype.hasOwnProperty.call(value, "Key") ||
        Object.prototype.hasOwnProperty.call(value, "ValueFrom")) &&
      (Object.prototype.hasOwnProperty.call(value, "Value") ||
        Object.prototype.hasOwnProperty.call(value, "Data") ||
        Object.prototype.hasOwnProperty.call(value, "DefaultValue"))
    );
  };

  const collectValue = (value: any) => {
    if (value === null || value === undefined || value === "") return;
    if (["string", "number", "boolean"].includes(typeof value)) {
      values.add(String(value).toLowerCase());
      return;
    }
    if (Array.isArray(value)) {
      value.forEach(collectValue);
      return;
    }
    if (typeof value === "object") {
      collectReferenceTokens(value);

      if (isLogicOptionRecord(value)) {
        collectValue(value.Value);
        collectValue(value.Data);
        collectValue(value.DefaultValue);
        collectValue(value.ValueFrom);
      }

      Object.entries(value).forEach(([key, entryValue]) => {
        if (/(value|uuid|field|fieldkey)$/i.test(key)) {
          collectValue(entryValue);
          return;
        }

        if (Array.isArray(entryValue) || typeof entryValue === "object") {
          collectValue(entryValue);
        }
      });
    }
  };

  collectValue(source);
  return Array.from(values);
};

const getSetupRuleConditions = (rule: any) => {
  return getSetupFirstOrderedArray(
    rule?.ConditionDetails,
    rule?.ComponentConfig?.Conditions,
    rule?.Conditions
  );
};

const getSetupRuleActions = (rule: any) => {
  return getSetupOrderedArray(rule?.Actions);
};

const getSetupListenerCondition = (listener: any) => {
  const listenerCondition = listener?.ListenerCondition;

  if (!listenerCondition) return null;
  if (listenerCondition.Condition) return listenerCondition.Condition;

  return listenerCondition.Type || listenerCondition.Options
    ? listenerCondition
    : null;
};

const getSetupWorkflowKeyValue = (workflow: any, workflowIndex: number) => {
  return String(
    workflow?.UUID ||
      workflow?.ID ||
      workflow?.Label ||
      workflow?.Name ||
      workflowIndex
  );
};

const getSolutionComponentLogicBranches = (
  solutionValue: SolutionType | undefined
) => {
  return (solutionValue?.Components || []).flatMap(
    (component: any, componentIndex: number) => {
      const eventHandlers = getSetupOrderedArray(
        component?.Item?.EventHandlers
      );
      const workflows = getSetupOrderedArray(component?.Item?.Workflows);
      const eventHandlersByUuid = new Map<string, any>();
      const eventHandlersById = new Map<string, any>();

      eventHandlers.forEach((eventHandler: any) => {
        if (eventHandler?.UUID)
          eventHandlersByUuid.set(String(eventHandler.UUID), eventHandler);
        if (eventHandler?.ID !== undefined && eventHandler?.ID !== null) {
          eventHandlersById.set(String(eventHandler.ID), eventHandler);
        }
      });

      const linkedBranches = workflows.flatMap(
        (workflow: any, workflowIndex: number) =>
          getSetupOrderedArray(workflow?.EventListeners).flatMap(
            (listener: any, listenerIndex: number) => {
              const eventHandler =
                (listener?.EventHandlerUUID
                  ? eventHandlersByUuid.get(String(listener.EventHandlerUUID))
                  : null) ||
                (listener?.EventHandlerID !== undefined &&
                listener?.EventHandlerID !== null
                  ? eventHandlersById.get(String(listener.EventHandlerID))
                  : null);

              return getSetupOrderedArray(eventHandler?.Rules).map(
                (rule: any, ruleIndex: number) => ({
                  component,
                  componentIndex,
                  workflow,
                  workflowKey: getSetupWorkflowKeyValue(
                    workflow,
                    workflowIndex
                  ),
                  workflowIndex,
                  listener,
                  listenerIndex,
                  eventHandler,
                  eventType: listener?.EventType,
                  rule,
                  ruleIndex,
                })
              );
            }
          )
      );

      const linkedEventHandlerUuids = new Set(
        linkedBranches
          .map((branch: any) => branch.eventHandler?.UUID)
          .filter(Boolean)
          .map(String)
      );
      const fallbackBranches = eventHandlers
        .filter(
          (eventHandler: any) =>
            !eventHandler?.UUID ||
            !linkedEventHandlerUuids.has(String(eventHandler.UUID))
        )
        .flatMap((eventHandler: any, eventHandlerIndex: number) =>
          getSetupOrderedArray(eventHandler?.Rules).map(
            (rule: any, ruleIndex: number) => ({
              component,
              componentIndex,
              workflow: null,
              workflowKey: null,
              workflowIndex: undefined,
              listener: null,
              listenerIndex: eventHandlerIndex,
              eventHandler,
              eventType: eventHandler?.EventType || "Unlinked event",
              rule,
              ruleIndex,
            })
          )
        );

      return [...linkedBranches, ...fallbackBranches];
    }
  );
};

const normalizeDefaultInputValues = (value: any) => {
  if (value === null || value === undefined || value === "") return undefined;
  if (Array.isArray(value)) return value.map((item) => String(item));
  return [String(value)];
};

const normalizeBusinessHoursDefaultInputValues = (value: any) => {
  if (typeof value === "string" && value !== "") return [value];
  return [JSON.stringify(value ?? getDefaultBusinessHours())];
};

const hasSetupInputValue = (value: any) =>
  Array.isArray(value) ? value.length > 0 : value !== undefined;

const isSameSetupInputValue = (firstValue: any, secondValue: any) => {
  if (Array.isArray(firstValue) || Array.isArray(secondValue)) {
    const firstArray = Array.isArray(firstValue) ? firstValue : [firstValue];
    const secondArray = Array.isArray(secondValue) ? secondValue : [secondValue];
    return (
      firstArray.length === secondArray.length &&
      firstArray.every((value, index) => value === secondArray[index])
    );
  }

  return firstValue === secondValue;
};

const mergeSetupFormDefaults = (
  currentFormData: Record<string, any>,
  defaultFormData: Record<string, any>
) => {
  const mergedFormData = { ...defaultFormData };

  Object.entries(currentFormData || {}).forEach(([componentUuid, fields]) => {
    mergedFormData[componentUuid] = {
      ...(mergedFormData[componentUuid] || {}),
      ...(fields || {}),
    };
  });

  return mergedFormData;
};

const getComponentInputArgumentDefaults = (
  solutionValue: SolutionType | undefined
) => {
  return (solutionValue?.Components || []).reduce(
    (defaults: Record<string, any>, component: any) => {
      const componentUuid = component?.ComponentUuid;
      if (!componentUuid) return defaults;

      (component?.Item?.InputArguments || []).forEach((argument: any) => {
        if (!argument?.Field) return;

        const defaultValue = argument.Default ?? argument.DefaultValue;
        const values =
          argument.Format === "business-hours"
            ? normalizeBusinessHoursDefaultInputValues(defaultValue)
            : normalizeDefaultInputValues(defaultValue);
        if (!values) return;

        defaults[componentUuid] = {
          ...(defaults[componentUuid] || {}),
          [argument.Field]: values,
        };
      });

      return defaults;
    },
    {}
  );
};

const SolutionSetup: React.FC<SolutionSetupProps> = ({
  solution,
  setSolution = undefined,
  importedJSON: importedJSONProp = undefined,
  setImportedJSON: setImportedJSONProp = undefined,
  manualFields: manualFieldsProp = undefined,
  setManualFields: setManualFieldsProp = undefined,
  automaticallySelectedFields = [],
  setAutomaticallySelectedFields = undefined,
  initialVariableDefaults = undefined,
  onChange = undefined,
  onMonitorsChange = undefined,
  onInboundAPIsChange = undefined,
  onInboundCampaignsChange = undefined,
  showDefaultValueInputsOnly = false,
}) => {
  const [localImportedJSON, setLocalImportedJSON] = React.useState<any>({
    Bag: { Internal: {}, External: {} },
    Item: {},
  });
  const importedJSON = importedJSONProp ?? localImportedJSON;
  const setImportedJSON = setImportedJSONProp ?? setLocalImportedJSON;

  const [localManualFields, setLocalManualFields] = useState<string[]>([]);
  const manualFields = manualFieldsProp ?? localManualFields;
  const setManualFields = setManualFieldsProp ?? setLocalManualFields;

  useEffect(() => {
    if (!solution) return;
    const { importedJSON: parsed } = parseSolutionData(solution);
    setImportedJSON(parsed);
  }, [solution]);

  const [apiProfiles, setApiProfiles] = useState<any>({});
  const [monitors, setMonitors] = useState<any>({});
  const [inboundAPIs, setInboundAPIs] = useState<any>({});
  const [inboundCampaigns, setInboundCampaigns] = useState<any>({});

  useEffect(() => {
    if (!solution?.Components) return;
    solution.Components.forEach((comp: any) => {
      if (comp.Role === "weather-monitor") {
        setMonitors((prev: any) => ({
          ...prev,
          ...initializeWeatherMonitor(comp.ComponentUuid),
        }));
      }
      if (comp.Role === "ping-monitor") {
        setMonitors((prev: any) => ({
          ...prev,
          ...initializePingMonitor(comp.ComponentUuid),
        }));
      }
      if (comp.Role === "inbound-message") {
        setInboundCampaigns((prev: any) => ({
          ...prev,
          ...initializeInboundCampaign(comp.ComponentUuid),
        }));
      }
    });
  }, [solution?.Components]);

  useEffect(() => {
    onMonitorsChange?.(monitors);
  }, [monitors, onMonitorsChange]);
  useEffect(() => {
    onInboundAPIsChange?.(inboundAPIs);
  }, [inboundAPIs, onInboundAPIsChange]);
  useEffect(() => {
    onInboundCampaignsChange?.(inboundCampaigns);
  }, [inboundCampaigns, onInboundCampaignsChange]);
  const { data: contactSetsData } = useGetContactSetsQuery({
    page: 0,
    perPage: 100,
  });
  const { data: reportingTopicsData } = useGetReportingTopicsQuery({
    pagination: { page: 0, perPage: 100 },
  });
  const { data: apiDataCollectionsData } = useGetApiDataCollectionsQuery({
    search: "",
    offset: 0,
    limit: 100,
  });
  const [updateApiConnection] = useUpdateApiConnectionMutation();
  const [getAiResponse] = usePostAiPromptMutation();
  const [isAiAssistantEnabled, setIsAiAssistantEnabled] = useState(false);
  const [isAiAssistantSidebarOpen, setIsAiAssistantSidebarOpen] =
    useState(false);
  const [isAiAssistantLoading, setIsAiAssistantLoading] = useState(false);
  const [aiAssistantSuggestions, setAiAssistantSuggestions] = useState<
    SetupAiSuggestion[]
  >([]);
  const [aiAssistantError, setAiAssistantError] = useState("");
  const [activeStepIndex, setActiveStepIndex] = useState(0);
  const [navDragFromIndex, setNavDragFromIndex] = useState<number | null>(null);
  const [navDropRegion, setNavDropRegion] = useState<{
    index: number;
    side: "left" | "right";
  } | null>(null);
  const [navDragGhost, setNavDragGhost] = useState<NavDragGhostState | null>(
    null
  );
  const isAdminMode = !!setSolution;
  const isUserMode = !isAdminMode;
  const disableInputEditing = isAdminMode;
  const disableInputDragging = !isAdminMode;
  const componentInputArgumentDefaults = React.useMemo(
    () => (isUserMode ? getComponentInputArgumentDefaults(solution) : {}),
    [isUserMode, solution]
  );
  const [formData, setFormData] = useState<Record<string, any>>(
    isUserMode
      ? mergeSetupFormDefaults(
          initialVariableDefaults || {},
          componentInputArgumentDefaults
        )
      : initialVariableDefaults || {}
  );
  useEffect(() => {
    if (!isUserMode) return;

    setFormData((prevFormData) => {
      const nextFormData = mergeSetupFormDefaults(
        prevFormData,
        componentInputArgumentDefaults
      );

      Object.entries(initialVariableDefaults || {}).forEach(
        ([componentUuid, fields]) => {
          Object.entries(fields || {}).forEach(([field, value]) => {
            const previousValue = prevFormData?.[componentUuid]?.[field];
            const componentDefaultValue =
              componentInputArgumentDefaults?.[componentUuid]?.[field];

            if (
              !hasSetupInputValue(previousValue) ||
              isSameSetupInputValue(previousValue, componentDefaultValue)
            ) {
              nextFormData[componentUuid] = {
                ...(nextFormData[componentUuid] || {}),
                [field]: value,
              };
            }
          });
        }
      );

      const hasChanged = Object.entries(nextFormData).some(
        ([componentUuid, fields]) =>
          Object.entries(fields || {}).some(
            ([field, value]) =>
              !isSameSetupInputValue(
                prevFormData?.[componentUuid]?.[field],
                value
              )
          )
      );

      return hasChanged ? nextFormData : prevFormData;
    });
  }, [componentInputArgumentDefaults, initialVariableDefaults, isUserMode]);

  useEffect(() => {
    if (onChange) {
      onChange(formData);
    }
  }, [formData, onChange]);

  // ── sidebar ghost drag ───────────────────────────────────────────────────
  const sidebarGhostEl = useRef<HTMLDivElement | null>(null);
  const draggingSidebarArg = useRef<SidebarDragArg | null>(null);
  const [sidebarDragState, setSidebarDragState] =
    useState<SidebarDragState | null>(null);
  const [sidebarOverInput, setSidebarOverInput] =
    useState<SidebarOverInputState | null>(null); // { groupId, index } | null

  // Track focused step and input group for diagram highlighting
  const [focusedStepIndex, setFocusedStepIndex] = useState<number | null>(null);
  const [focusedInputGroupId, setFocusedInputGroupId] = useState<string | null>(
    null
  );
  const [focusedInputNodeKey, setFocusedInputNodeKey] = useState<string | null>(
    null
  );
  const [focusedLogicNodeKey, setFocusedLogicNodeKey] = useState<string | null>(
    null
  );
  const [activeDiagramView, setActiveDiagramView] = useState<
    "solutionSteps" | "componentFlow"
  >("solutionSteps");
  const setupFormContainerRef = useRef<HTMLDivElement | null>(null);
  const [pendingFlowSelection, setPendingFlowSelection] =
    useState<PendingFlowSelection | null>(null);
  const [diagramInputModal, setDiagramInputModal] =
    useState<DiagramInputModalState | null>(null);
  const setupAiSignature = React.useMemo(
    () => JSON.stringify(solution?.ComponentConfig?.steps || []),
    [solution?.ComponentConfig?.steps]
  );
  const lastAiRefreshSignatureRef = useRef<string | null>(null);
  const previousSetupAiSignatureRef = useRef<string | null>(setupAiSignature);
  const aiAssistantArmedSignatureRef = useRef<string | null>(null);
  const skipNextAiAutoRefreshRef = useRef(false);

  const applyAiAssistantChanges = useCallback(
    (changes: NonNullable<SetupAiSuggestion["action"]>["changes"] = []) => {
      if (!setSolution || changes.length === 0) return false;

      let applied = false;
  let appliedSelection: FlowNodeSelection | null = null;

      setSolution((prev: SolutionType) => {
        const currentSteps = prev?.ComponentConfig?.steps || [];
        const textResult = applySetupTextChanges(currentSteps, changes);
        applied = textResult.applied;

        return applied
          ? {
              ...prev,
              ComponentConfig: {
                ...prev.ComponentConfig,
                steps: textResult.steps,
              },
            }
          : prev;
      });

      return applied;
    },
    [setSolution]
  );

  const applyAiAssistantCompletion = useCallback(
    (
      completionSteps: NonNullable<SetupAiSuggestion["action"]>["steps"] = [],
      changes: NonNullable<SetupAiSuggestion["action"]>["changes"] = []
    ) => {
      if (!setSolution || (!completionSteps.length && !changes.length)) {
        return false;
      }

      let applied = false;

      setSolution((prev: SolutionType) => {
        const currentSteps = prev?.ComponentConfig?.steps || [];
        const availableGroupTemplates = getSetupCompletionGroupTemplates(prev);
        const completionResult = applySetupCompletionSteps(
          currentSteps,
          completionSteps,
          availableGroupTemplates
        );
        const textResult = applySetupTextChanges(
          completionResult.steps,
          changes
        );

        applied = completionResult.applied || textResult.applied;

        return applied
          ? {
              ...prev,
              ComponentConfig: {
                ...prev.ComponentConfig,
                steps: textResult.steps,
              },
            }
          : prev;
      });

      return applied;
    },
    [setSolution]
  );

  const requestAiAssistantSuggestions = useCallback(async (mode: "change" | "full" | "complete") => {
    aiAssistantArmedSignatureRef.current = null;
    lastAiRefreshSignatureRef.current = setupAiSignature;
    setAiAssistantError("");
    setIsAiAssistantLoading(true);
    if (mode !== "change") {
      setAiAssistantSuggestions([]);
    }

    try {
      const currentSteps = solution?.ComponentConfig?.steps || [];
      const previousSteps = previousSetupAiSignatureRef.current
        ? JSON.parse(previousSetupAiSignatureRef.current)
        : [];
      const changedFields =
        mode === "change"
          ? getSetupChangedFields(previousSteps, currentSteps)
          : [];

      if (mode === "change" && changedFields.length === 0) {
        setAiAssistantError("No text changes to review.");
        return;
      }

      const stage =
        mode === "change"
          ? SETUP_AI_CHANGE_STAGE
          : mode === "complete"
          ? SETUP_AI_COMPLETION_STAGE
          : SETUP_AI_FLOW_STAGE;

      if (mode === "change") {
        let suggestionCount = 0;
        let failedRequestCount = 0;

        await Promise.all(
          changedFields.map(async (changedField) => {
            try {
              const response = await getAiResponse({
                prompt: buildSetupChangeAiPrompt([changedField]),
                type: `solution-setup-improvements-${stage.key}`,
                model: "gpt-5.5",
              }).unwrap();
              const answer = response?.answers?.[0] || response;
              const suggestions = parseSetupAiSuggestions(answer, stage).slice(
                0,
                SETUP_AI_MAX_SUGGESTIONS
              );

              if (suggestions.length === 0) return;

              suggestionCount += suggestions.length;
              setAiAssistantSuggestions((prevSuggestions) => {
                const getSuggestionTargetKey = (suggestion: SetupAiSuggestion) => {
                  const change = suggestion.action?.change;

                  if (!change) return "";

                  return [
                    change.target,
                    change.stepIndex ?? "",
                    change.groupId ?? "",
                    change.inputIndex ?? "",
                    change.field ?? "",
                  ].join(":");
                };
                const replacementKeys = new Set(
                  suggestions
                    .map(getSuggestionTargetKey)
                    .filter(Boolean)
                );
                const retainedSuggestions = replacementKeys.size
                  ? prevSuggestions.filter(
                      (suggestion) =>
                        !replacementKeys.has(getSuggestionTargetKey(suggestion))
                    )
                  : prevSuggestions;
                const seenSuggestions = new Set(
                  retainedSuggestions.map((suggestion) =>
                    `${suggestion.title}:${suggestion.detail}`
                      .trim()
                      .toLowerCase()
                  )
                );
                const nextSuggestions = [...retainedSuggestions];

                suggestions.forEach((suggestion) => {
                  const suggestionKey = `${suggestion.title}:${suggestion.detail}`
                    .trim()
                    .toLowerCase();

                  if (seenSuggestions.has(suggestionKey)) return;

                  seenSuggestions.add(suggestionKey);
                  nextSuggestions.push(suggestion);
                });

                return nextSuggestions;
              });
            } catch (error) {
              failedRequestCount += 1;
            }
          })
        );

        if (suggestionCount === 0) {
          setAiAssistantError(
            failedRequestCount === changedFields.length
              ? "Unable to load AI Assistant suggestions."
              : "No wording improvements were suggested for this change."
          );
        }
        return;
      }

      const response = await getAiResponse({
        prompt:
          mode === "complete"
            ? buildSetupCompletionAiPrompt(solution, SETUP_AI_COMPLETION_STAGE)
            : buildSetupAiPrompt(solution, SETUP_AI_FLOW_STAGE),
        type: `solution-setup-improvements-${stage.key}`,
        model: "gpt-5.5",
      }).unwrap();
      const answer = response?.answers?.[0] || response;
      const aiSuggestions = parseSetupAiSuggestions(
        answer,
        stage
      ).slice(0, SETUP_AI_MAX_SUGGESTIONS);
      const seenSuggestions = new Set<string>();
      const suggestions = aiSuggestions
        .filter((suggestion) => {
          const suggestionKey = `${suggestion.title}:${suggestion.detail}`
            .trim()
            .toLowerCase();
          if (seenSuggestions.has(suggestionKey)) return false;
          seenSuggestions.add(suggestionKey);
          return true;
        })
        .slice(0, SETUP_AI_MAX_SUGGESTIONS);

      if (mode === "complete") {
        const completionSteps = suggestions.flatMap(
          (suggestion) => suggestion.action?.steps || []
        );
        const completionChanges = suggestions.flatMap(
          (suggestion) => suggestion.action?.changes || []
        );
        const applied = applyAiAssistantCompletion(
          completionSteps,
          completionChanges
        );

        skipNextAiAutoRefreshRef.current = applied;
        setAiAssistantSuggestions([]);
        setAiAssistantError(
          applied
            ? "Setup completion changes were applied."
            : "No setup completion changes were applied."
        );
        return;
      }

      setAiAssistantSuggestions(suggestions);
      if (suggestions.length === 0) {
        setAiAssistantError("No setup improvements were suggested.");
      }
    } catch (error) {
      setAiAssistantSuggestions([]);
      setAiAssistantError("Unable to load AI Assistant suggestions.");
    } finally {
      previousSetupAiSignatureRef.current = setupAiSignature;
      setIsAiAssistantLoading(false);
    }
  }, [applyAiAssistantCompletion, getAiResponse, setupAiSignature, solution]);

  const handleAiAssistantToggle = useCallback(
    (checked: boolean) => {
      setIsAiAssistantEnabled(checked);
      setAiAssistantError("");

      if (!checked) {
        setIsAiAssistantSidebarOpen(false);
        setAiAssistantSuggestions([]);
        aiAssistantArmedSignatureRef.current = null;
        return;
      }

      aiAssistantArmedSignatureRef.current = setupAiSignature;
    },
    [setupAiSignature]
  );

  const handleOpenAiAssistantSidebar = useCallback(() => {
    setIsAiAssistantSidebarOpen(true);
    aiAssistantArmedSignatureRef.current = null;
  }, []);

  useEffect(() => {
    if (!isAiAssistantEnabled) return;
    if (aiAssistantArmedSignatureRef.current === setupAiSignature) return;
    if (lastAiRefreshSignatureRef.current === setupAiSignature) return;

    if (skipNextAiAutoRefreshRef.current) {
      skipNextAiAutoRefreshRef.current = false;
      lastAiRefreshSignatureRef.current = setupAiSignature;
      return;
    }

    const refreshTimer = window.setTimeout(() => {
      const currentSteps = solution?.ComponentConfig?.steps || [];
      const previousSteps = previousSetupAiSignatureRef.current
        ? JSON.parse(previousSetupAiSignatureRef.current)
        : [];
      const changedFields = getSetupChangedFields(previousSteps, currentSteps);

      if (changedFields.length === 0) {
        previousSetupAiSignatureRef.current = setupAiSignature;
        lastAiRefreshSignatureRef.current = setupAiSignature;
        return;
      }

      requestAiAssistantSuggestions("change");
    }, SETUP_AI_CHANGE_DEBOUNCE_MS);

    return () => {
      window.clearTimeout(refreshTimer);
    };
  }, [isAiAssistantEnabled, requestAiAssistantSuggestions, setupAiSignature, solution]);

  const handleApplyAiAssistantSuggestion = useCallback(
    (suggestion: SetupAiSuggestion) => {
      const suggestedChange = suggestion.action?.change;
      const suggestedSteps = suggestion.action?.steps || [];

      if (!setSolution || (!suggestedChange && suggestedSteps.length === 0)) {
        setAiAssistantError(
          "This suggestion cannot be applied automatically."
        );
        return;
      }

      let applied = false;
      let appliedSelection: FlowNodeSelection | null = null;

      if (suggestedChange) {
        const stepIndex = Number(suggestedChange.stepIndex);
        const inputIndex = Number(suggestedChange.inputIndex);

        if (Number.isInteger(stepIndex)) {
          if (suggestedChange.target === "step") {
            appliedSelection = { nodeKind: "step", stepIndex };
          } else if (suggestedChange.target === "group" && suggestedChange.groupId) {
            appliedSelection = {
              nodeKind: "group",
              stepIndex,
              groupId: suggestedChange.groupId,
            };
          } else if (
            suggestedChange.target === "input" &&
            suggestedChange.groupId &&
            Number.isInteger(inputIndex)
          ) {
            appliedSelection = {
              nodeKind: "input",
              stepIndex,
              groupId: suggestedChange.groupId,
              inputIndex,
            };
          }
        }
      } else {
        suggestedSteps.some((step: any, stepIndex: number) => {
          const groupId = [
            ...(step.groupIds || []),
            ...(step.inputGroupIds || []),
            step.groupId,
          ].find(Boolean);

          if (!groupId) return false;

          appliedSelection = {
            nodeKind: "group",
            stepIndex,
            groupId,
          };
          return true;
        });
      }

      setSolution((prev: SolutionType) => {
        const currentSteps = prev?.ComponentConfig?.steps || [];

        if (suggestedChange) {
          const stepIndex = Number(suggestedChange.stepIndex);
          const fieldNameByTarget = {
            step: {
              name: "name",
              description: "description",
              label: "name",
            },
            group: {
              name: "name",
              description: "description",
              label: "name",
            },
            input: {
              name: "Name",
              description: "Description",
              label: "Label",
            },
          } as const;
          const fieldName =
            fieldNameByTarget[suggestedChange.target]?.[
              suggestedChange.field
            ];

          if (
            !fieldName ||
            !suggestedChange.to ||
            !Number.isInteger(stepIndex) ||
            !currentSteps[stepIndex]
          ) {
            return prev;
          }

          const nextSteps = currentSteps.map((step: any, index: number) => {
            if (index !== stepIndex) return step;

            if (suggestedChange.target === "step") {
              return {
                ...step,
                [fieldName]: suggestedChange.to,
              };
            }

            if (suggestedChange.target === "group") {
              return {
                ...step,
                inputGroups: (step.inputGroups || []).map((group: any) =>
                  group?.id === suggestedChange.groupId
                    ? {
                        ...group,
                        [fieldName]: suggestedChange.to,
                      }
                    : group
                ),
              };
            }

            const inputIndex = Number(suggestedChange.inputIndex);
            if (!Number.isInteger(inputIndex)) return step;

            return {
              ...step,
              inputGroups: (step.inputGroups || []).map((group: any) =>
                group?.id === suggestedChange.groupId
                  ? {
                      ...group,
                      inputs: (group.inputs || []).map(
                        (input: any, index: number) =>
                          index === inputIndex
                            ? {
                                ...input,
                                [fieldName]: suggestedChange.to,
                              }
                            : input
                      ),
                    }
                  : group
              ),
            };
          });

          applied = true;

          return {
            ...prev,
            ComponentConfig: {
              ...prev.ComponentConfig,
              steps: nextSteps,
            },
          };
        }

        const groupsById = new Map<string, any>();
        const usedGroupIds = new Set<string>();

        currentSteps.forEach((step: any) => {
          (step.inputGroups || []).forEach((group: any) => {
            if (group?.id) groupsById.set(group.id, group);
          });
        });

        const nextSteps = suggestedSteps.flatMap((step: any) => {
          const groupIds = [
            ...(step.groupIds || []),
            ...(step.inputGroupIds || []),
            step.groupId,
          ].filter(Boolean);
          const inputGroups = groupIds
            .filter((groupId: string) => !usedGroupIds.has(groupId))
            .map((groupId: string) => {
              const group = groupsById.get(groupId);
              if (!group) return null;
              usedGroupIds.add(groupId);
              return group;
            })
            .filter(Boolean);

          if (inputGroups.length === 0) return [];

          return [
            {
              name:
                step.name || inputGroups[0]?.name || suggestion.title,
              description:
                step.description ||
                inputGroups[0]?.description ||
                suggestion.detail,
              inputGroups,
            },
          ];
        });

        if (nextSteps.length === 0) return prev;

        currentSteps.forEach((step: any) => {
          const remainingGroups = (step.inputGroups || []).filter(
            (group: any) => !usedGroupIds.has(group?.id)
          );
          if (remainingGroups.length === 0) return;

          nextSteps.push({
            ...step,
            inputGroups: remainingGroups,
          });
        });

        applied = true;

        return {
          ...prev,
          ComponentConfig: {
            ...prev.ComponentConfig,
            steps: nextSteps,
          },
        };
      });

      if (applied) {
        skipNextAiAutoRefreshRef.current = true;
        lastAiRefreshSignatureRef.current = JSON.stringify(
          solution?.ComponentConfig?.steps || []
        );
        setAiAssistantSuggestions((prevSuggestions) =>
          prevSuggestions.filter((item) => item !== suggestion)
        );
        setAiAssistantError("");
        if (appliedSelection) {
          const stepIndex = appliedSelection.stepIndex ?? 0;
          setActiveStepIndex(stepIndex);
          setFocusedStepIndex(stepIndex);
          setFocusedLogicNodeKey(null);

          if (appliedSelection.nodeKind === "step") {
            setFocusedInputGroupId(null);
            setFocusedInputNodeKey(null);
            setPendingFlowSelection(null);
          } else if (appliedSelection.nodeKind === "group") {
            setFocusedInputGroupId(
              `step:${stepIndex}|group:${appliedSelection.groupId}`
            );
            setFocusedInputNodeKey(null);
            setPendingFlowSelection({
              ...appliedSelection,
              stepIndex,
              attempts: 0,
            });
          } else {
            setFocusedInputGroupId(
              `step:${stepIndex}|group:${appliedSelection.groupId}`
            );
            setFocusedInputNodeKey(null);
            setPendingFlowSelection({
              ...appliedSelection,
              stepIndex,
              attempts: 0,
            });
          }
        }
        return;
      }

      setAiAssistantError(
        "This suggestion did not match the current setup groups."
      );
    },
    [setSolution, solution?.ComponentConfig?.steps]
  );

  const getInputFocusKey = useCallback(
    (stepIndex: number, inputGroupId: string, input: any) => {
      if (!inputGroupId) return null;
      const identity =
        input?.UUID ||
        (input?.ComponentUuid && input?.Field
          ? `${input.ComponentUuid}:${input.Field}`
          : null) ||
        (input?.ComponentUuid && input?.Name
          ? `${input.ComponentUuid}:${input.Name}`
          : null) ||
        (input?.ComponentUuid && input?.Label
          ? `${input.ComponentUuid}:${input.Label}`
          : null) ||
        (input?.__focusIndex !== undefined ? `${input.__focusIndex}` : null) ||
        "unknown";
      return `step:${stepIndex}|group:${inputGroupId}|input:${identity}`;
    },
    []
  );

  const findInputCardElement = useCallback(
    (container: HTMLElement, groupId: string, inputIndex: number) => {
      const stepsContent =
        container.querySelector<HTMLElement>("[data-setup-steps-content]") ||
        container;
      const activePane =
        stepsContent.querySelector<HTMLElement>(".tab-pane.active") ||
        stepsContent;
      const inputTarget = `${groupId}:${inputIndex}`;
      const escapedInputTarget =
        typeof CSS !== "undefined" && CSS.escape
          ? CSS.escape(inputTarget)
          : inputTarget.replace(/"/g, '\\"');
      const directTarget = activePane.querySelector<HTMLElement>(
        `[data-input-target="${escapedInputTarget}"]`
      );

      if (directTarget) return directTarget;

      const cards = Array.from(
        activePane.querySelectorAll<HTMLElement>(
          "[data-group-id][data-input-item]"
        )
      );

      return (
        cards.find(
          (card) =>
            card.dataset.groupId === groupId &&
            Number(card.dataset.inputItem) === inputIndex
        ) || null
      );
    },
    []
  );

  const findStepElement = useCallback(
    (container: HTMLElement, stepIndex: number) => {
      const stepsContent =
        container.querySelector<HTMLElement>("[data-setup-steps-content]") ||
        container;
      const activePane =
        stepsContent.querySelector<HTMLElement>(".tab-pane.active") ||
        stepsContent;
      return (
        activePane.querySelector<HTMLElement>(`[data-step-target="${stepIndex}"]`) ||
        null
      );
    },
    []
  );

  const findGroupZoneElement = useCallback(
    (container: HTMLElement, groupId: string) => {
      const stepsContent =
        container.querySelector<HTMLElement>("[data-setup-steps-content]") ||
        container;
      const activePane =
        stepsContent.querySelector<HTMLElement>(".tab-pane.active") ||
        stepsContent;
      const zones = Array.from(
        activePane.querySelectorAll<HTMLElement>(
          "[data-input-group-target], [data-input-zone]"
        )
      );

      return (
        zones.find(
          (zone) =>
            zone.dataset.inputGroupTarget === groupId ||
            zone.dataset.inputZone === groupId
        ) || null
      );
    },
    []
  );

  const scrollSetupFormToTop = useCallback(() => {
    const container = setupFormContainerRef.current;
    if (!container) return;

    window.requestAnimationFrame(() => {
      container.scrollTo({ top: 0, behavior: "auto" });
    });
  }, []);

  const scrollElementToSetupTop = useCallback((targetEl: HTMLElement) => {
    const container = setupFormContainerRef.current;

    if (!container) {
      targetEl.scrollIntoView({ behavior: "auto", block: "start" });
      return;
    }

    const containerRect = container.getBoundingClientRect();
    const targetRect = targetEl.getBoundingClientRect();
    const topOffset = 12;

    container.scrollTo({
      top: Math.max(
        0,
        container.scrollTop + targetRect.top - containerRect.top - topOffset
      ),
      behavior: "auto",
    });
  }, []);

  const focusFirstEditableField = useCallback((cardEl: HTMLElement) => {
    const firstField = cardEl.querySelector<HTMLElement>(
      'input:not([type="hidden"]):not([disabled]), textarea:not([disabled]), select:not([disabled]), [role="combobox"], [aria-autocomplete], [contenteditable="true"]'
    );
    firstField?.focus({ preventScroll: true });
  }, []);

  const focusStep = useCallback((stepIndex: number, scrollToTop = true) => {
    setActiveStepIndex(stepIndex);
    setFocusedStepIndex(stepIndex);
    setFocusedInputGroupId(null);
    setFocusedInputNodeKey(null);
    setFocusedLogicNodeKey(null);
    if (scrollToTop) {
      scrollSetupFormToTop();
    }
  }, [scrollSetupFormToTop]);

  const handleFlowNodeClick = useCallback(
    (selection: FlowNodeSelection) => {
      if (selection?.stepIndex === undefined || selection?.stepIndex === null) {
        return;
      }

      const stepIndex = selection.stepIndex;
      const step = solution?.ComponentConfig?.steps?.[stepIndex];
      if (!step) return;

      focusStep(stepIndex, selection.nodeKind === "step");
      setFocusedLogicNodeKey(null);

      if (selection.nodeKind === "step") {
        setPendingFlowSelection({ ...selection, attempts: 0 });
        return;
      }

      if (selection.nodeKind === "group") {
        if (!selection.groupId) return;
        setFocusedInputGroupId(`step:${stepIndex}|group:${selection.groupId}`);
        setFocusedInputNodeKey(null);
        setPendingFlowSelection({ ...selection, attempts: 0 });
        return;
      }

      if (selection.nodeKind === "input") {
        if (!selection.groupId || typeof selection.inputIndex !== "number") {
          return;
        }

        const group = (step.inputGroups || []).find(
          (item: any) => item.id === selection.groupId
        );
        const input = group?.inputs?.[selection.inputIndex];
        const resolvedInputNodeKey =
          selection.inputFocusKey ||
          (input
            ? getInputFocusKey(stepIndex, selection.groupId, {
                ...input,
                __focusIndex: selection.inputIndex,
              })
            : null);

        setFocusedInputGroupId(`step:${stepIndex}|group:${selection.groupId}`);
        setFocusedInputNodeKey(resolvedInputNodeKey);
        if (selection.action === "edit") {
          if (selection.isFullscreen) {
            setDiagramInputModal({
              stepIndex,
              groupId: selection.groupId,
              inputIndex: selection.inputIndex,
            });
          }
          setPendingFlowSelection(null);
          return;
        }

        setPendingFlowSelection({ ...selection, attempts: 0 });
      }
    },
    [focusStep, getInputFocusKey, solution?.ComponentConfig?.steps]
  );

  const handleAiAssistantSuggestionClick = useCallback(
    (suggestion: SetupAiSuggestion) => {
      const suggestedChange = suggestion.action?.change;
      const suggestedSteps = suggestion.action?.steps || [];

      if (suggestedChange) {
        const stepIndex = Number(suggestedChange.stepIndex);
        const step = solution?.ComponentConfig?.steps?.[stepIndex];
        if (!Number.isInteger(stepIndex) || !step) return;

        if (suggestedChange.target === "step") {
          handleFlowNodeClick({ nodeKind: "step", stepIndex });
          return;
        }

        if (suggestedChange.target === "group") {
          if (!suggestedChange.groupId) return;
          handleFlowNodeClick({
            nodeKind: "group",
            stepIndex,
            groupId: suggestedChange.groupId,
          });
          return;
        }

        const inputIndex = Number(suggestedChange.inputIndex);
        if (!suggestedChange.groupId || !Number.isInteger(inputIndex)) return;

        handleFlowNodeClick({
          nodeKind: "input",
          stepIndex,
          groupId: suggestedChange.groupId,
          inputIndex,
        });
        return;
      }

      const groupIds = suggestedSteps.flatMap((step: any) => [
        ...(step.groupIds || []),
        ...(step.inputGroupIds || []),
        step.groupId,
      ]);
      const targetGroupId = groupIds.find(Boolean);
      if (!targetGroupId) return;

      const stepIndex = (solution?.ComponentConfig?.steps || []).findIndex(
        (step: any) =>
          (step?.inputGroups || []).some(
            (group: any) => group?.id === targetGroupId
          )
      );
      if (stepIndex < 0) return;

      handleFlowNodeClick({
        nodeKind: "group",
        stepIndex,
        groupId: targetGroupId,
      });
    },
    [handleFlowNodeClick, solution?.ComponentConfig?.steps]
  );

  const handleUsageSelect = useCallback((usage: SolutionInputUsage) => {
    if (!usage?.logicNodeKey) return;

    setFocusedStepIndex(null);
    setFocusedInputGroupId(null);
    setFocusedInputNodeKey(null);
    setPendingFlowSelection(null);
    setActiveDiagramView("componentFlow");
    setFocusedLogicNodeKey(null);
    window.requestAnimationFrame(() => {
      setFocusedLogicNodeKey(usage.logicNodeKey);
    });
  }, []);

  useEffect(() => {
    if (!pendingFlowSelection) return;
    if (pendingFlowSelection.stepIndex !== activeStepIndex) return;

    const container = setupFormContainerRef.current;
    if (!container) return;

    let targetEl: HTMLElement | null = null;

    if (pendingFlowSelection.nodeKind === "step") {
      targetEl = findStepElement(container, pendingFlowSelection.stepIndex);
    } else if (
      pendingFlowSelection.nodeKind === "input" &&
      pendingFlowSelection.groupId &&
      typeof pendingFlowSelection.inputIndex === "number"
    ) {
      targetEl = findInputCardElement(
        container,
        pendingFlowSelection.groupId,
        pendingFlowSelection.inputIndex
      );
    } else if (
      pendingFlowSelection.nodeKind === "group" &&
      pendingFlowSelection.groupId
    ) {
      targetEl = findGroupZoneElement(container, pendingFlowSelection.groupId);
    }

    if (targetEl) {
      scrollElementToSetupTop(targetEl);
      if (pendingFlowSelection.nodeKind === "input") {
        focusFirstEditableField(targetEl);
      }
      setPendingFlowSelection(null);
      return;
    }

    if (pendingFlowSelection.attempts >= 6) {
      setPendingFlowSelection(null);
      return;
    }

    const retryTimer = window.setTimeout(() => {
      setPendingFlowSelection((prev) =>
        prev
          ? {
              ...prev,
              attempts: prev.attempts + 1,
            }
          : prev
      );
    }, 70);

    return () => {
      window.clearTimeout(retryTimer);
    };
  }, [
    activeStepIndex,
    findGroupZoneElement,
    findInputCardElement,
    findStepElement,
    focusFirstEditableField,
    pendingFlowSelection,
    scrollElementToSetupTop,
  ]);

  const remapIndexAfterMove = useCallback(
    (currentIndex: number, fromIndex: number, insertIndex: number) => {
      const finalIndex =
        fromIndex < insertIndex ? insertIndex - 1 : insertIndex;
      if (currentIndex === fromIndex) return finalIndex;

      if (fromIndex < finalIndex) {
        if (currentIndex > fromIndex && currentIndex <= finalIndex)
          return currentIndex - 1;
        return currentIndex;
      }

      if (fromIndex > finalIndex) {
        if (currentIndex >= finalIndex && currentIndex < fromIndex)
          return currentIndex + 1;
      }

      return currentIndex;
    },
    []
  );

  const handleNavStepDragStart = useCallback(
    (
      e: React.DragEvent<HTMLElement>,
      index: number,
      label: string,
      width: number,
      height: number
    ) => {
      if (!setSolution) return;

      e.dataTransfer.effectAllowed = "move";
      e.dataTransfer.setData("text/plain", String(index));

      const transparent = document.createElement("img");
      transparent.src =
        "data:image/svg+xml;charset=utf-8,%3Csvg xmlns='http://www.w3.org/2000/svg' width='1' height='1'%3E%3C/svg%3E";
      e.dataTransfer.setDragImage(transparent, 0, 0);

      setNavDragFromIndex(index);
      setNavDropRegion({ index, side: "right" });
      setNavDragGhost({
        label,
        top: e.clientY - height / 2,
        left: e.clientX - width / 2,
        width,
        height,
      });
    },
    [setSolution]
  );

  const handleNavStepDragOver = useCallback(
    (e: React.DragEvent<HTMLElement>, index: number) => {
      if (!setSolution) return;
      e.preventDefault();

      const rect = e.currentTarget.getBoundingClientRect();
      const side = e.clientX <= rect.left + rect.width / 2 ? "left" : "right";
      setNavDropRegion((prev) =>
        prev && prev.index === index && prev.side === side
          ? prev
          : { index, side }
      );

      setNavDragGhost((prev) =>
        prev
          ? {
              ...prev,
              top: e.clientY - prev.height / 2,
              left: e.clientX - prev.width / 2,
            }
          : prev
      );
    },
    [setSolution]
  );

  const resetNavDragState = useCallback(() => {
    setNavDragFromIndex(null);
    setNavDropRegion(null);
    setNavDragGhost(null);
  }, []);

  const handleNavStepDrop = useCallback(
    (toIndex: number, side: "left" | "right") => {
      if (!setSolution || navDragFromIndex === null) {
        resetNavDragState();
        return;
      }

      const fromIndex = navDragFromIndex;
      const insertIndex = side === "left" ? toIndex : toIndex + 1;
      const finalIndex =
        fromIndex < insertIndex ? insertIndex - 1 : insertIndex;
      if (fromIndex === finalIndex) {
        resetNavDragState();
        return;
      }

      setSolution((prevSolution: any) => {
        const prevSteps = prevSolution?.ComponentConfig?.steps || [];
        if (
          !prevSteps.length ||
          fromIndex < 0 ||
          insertIndex < 0 ||
          fromIndex >= prevSteps.length ||
          insertIndex > prevSteps.length
        ) {
          return prevSolution;
        }

        const reorderedSteps = [...prevSteps];
        const [movedStep] = reorderedSteps.splice(fromIndex, 1);
        const adjustedInsertIndex =
          fromIndex < insertIndex ? insertIndex - 1 : insertIndex;
        reorderedSteps.splice(adjustedInsertIndex, 0, movedStep);

        return {
          ...prevSolution,
          ComponentConfig: {
            ...prevSolution.ComponentConfig,
            steps: reorderedSteps,
          },
        };
      });

      setActiveStepIndex((prev) =>
        remapIndexAfterMove(prev, fromIndex, insertIndex)
      );
      setFocusedStepIndex((prev) =>
        prev === null ? null : remapIndexAfterMove(prev, fromIndex, insertIndex)
      );
      resetNavDragState();
    },
    [navDragFromIndex, remapIndexAfterMove, resetNavDragState, setSolution]
  );

  const getSidebarMergeRef = useCallback(
    (arg: any, fallbackComponentUuid?: string) => {
      if (!arg || typeof arg !== "object") return null;

      if (arg.__kind === "external") {
        if (!arg.fieldType || !arg.fieldKey) return null;
        return {
          __kind: "external",
          fieldType: arg.fieldType,
          fieldKey: arg.fieldKey,
          ComponentUuid: `external:${arg.fieldType}`,
          Field: arg.fieldKey,
        };
      }

      if (arg.__kind) return null;

      const componentUuid = fallbackComponentUuid || arg.ComponentUuid;
      if (!componentUuid || !arg.Field) return null;
      return { ComponentUuid: componentUuid, Field: arg.Field };
    },
    []
  );

  const canSidebarMergeWithInput = useCallback(
    (draggedArg: any, targetInput: any, draggedComponentUuid?: string) => {
      if (!draggedArg || !targetInput) return false;

      if (
        draggedArg?.__kind === "external" ||
        targetInput?.__kind === "external"
      ) {
        return (
          draggedArg?.__kind === "external" &&
          targetInput?.__kind === "external" &&
          !!draggedArg?.fieldType &&
          draggedArg.fieldType === targetInput.fieldType &&
          !!draggedArg?.fieldKey &&
          !!targetInput?.fieldKey &&
          draggedArg.fieldKey !== targetInput.fieldKey
        );
      }

      if (draggedArg?.__kind || targetInput?.__kind) return false;
      if (!draggedArg?.Format || !targetInput?.Format) return false;
      if (draggedArg.Format !== targetInput.Format) return false;

      const sourceRef = getSidebarMergeRef(draggedArg, draggedComponentUuid);
      const targetRef = getSidebarMergeRef(
        targetInput,
        targetInput?.ComponentUuid
      );
      if (!sourceRef || !targetRef) return false;

      return !(
        sourceRef.ComponentUuid === targetRef.ComponentUuid &&
        sourceRef.Field === targetRef.Field
      );
    },
    [getSidebarMergeRef]
  );

  const onSidebarArgPointerDown = useCallback(
    (
      e: React.PointerEvent<HTMLElement>,
      arg: SidebarDragArg,
      ComponentUuid?: string
    ) => {
      console.log("sidebar", e, arg, ComponentUuid);
      e.preventDefault();
      const rect = e.currentTarget.getBoundingClientRect();
      draggingSidebarArg.current = ComponentUuid
        ? { ...arg, ComponentUuid }
        : arg;

      setSidebarDragState({
        arg,
        top: e.clientY - rect.height / 2,
        left: rect.left,
        width: rect.width,
        height: rect.height,
      });

      const resolveTarget = (
        clientX: number,
        clientY: number
      ): HTMLElement | null => {
        if (sidebarGhostEl.current)
          sidebarGhostEl.current.style.pointerEvents = "none";
        const target = document.elementFromPoint(clientX, clientY);
        if (sidebarGhostEl.current)
          sidebarGhostEl.current.style.pointerEvents = "";
        return target as HTMLElement | null;
      };

      const onMove = (moveEvt: PointerEvent) => {
        if (sidebarGhostEl.current) {
          sidebarGhostEl.current.style.top = `${
            moveEvt.clientY - rect.height / 2
          }px`;
          sidebarGhostEl.current.style.left = `${
            moveEvt.clientX - rect.width / 2
          }px`;
        }

        const target = resolveTarget(moveEvt.clientX, moveEvt.clientY);
        if (!target) {
          setSidebarOverInput(null);
          return;
        }

        const inputEl = target.closest(
          "[data-input-item]"
        ) as HTMLElement | null;
        if (inputEl) {
          const gId = inputEl.dataset.groupId ?? "";
          if (gId === "") return;
          const idx = Number(inputEl.dataset.inputItem ?? 0);
          const itemRect = inputEl.getBoundingClientRect();
          const relativeY = moveEvt.clientY - itemRect.top;
          const topThirdEnd = itemRect.height * 0.33;
          const bottomThirdStart = itemRect.height * 0.67;
          const insertIdx =
            relativeY < topThirdEnd
              ? idx
              : relativeY > bottomThirdStart
              ? idx + 1
              : idx;

          const draggedArg = draggingSidebarArg.current;
          const targetInput = solution?.ComponentConfig?.steps
            ?.flatMap((step: any) => step.inputGroups || [])
            ?.find((group: any) => group.id === gId)?.inputs?.[idx];

          const canMerge = canSidebarMergeWithInput(
            draggedArg,
            targetInput,
            ComponentUuid
          );

          const nextOver =
            canMerge &&
            relativeY >= topThirdEnd &&
            relativeY <= bottomThirdStart
              ? {
                  groupId: gId,
                  index: insertIdx,
                  mode: "merge" as const,
                  targetIndex: idx,
                  mergeCandidateIndex: idx,
                }
              : {
                  groupId: gId,
                  index: insertIdx,
                  mode: "insert" as const,
                  targetIndex: undefined,
                  mergeCandidateIndex: undefined,
                };

          setSidebarOverInput((prev) =>
            prev?.groupId === gId &&
            prev?.index === insertIdx &&
            prev?.mode === nextOver.mode &&
            prev?.targetIndex === nextOver.targetIndex &&
            prev?.mergeCandidateIndex === nextOver.mergeCandidateIndex
              ? prev
              : nextOver
          );
          return;
        }
        const zoneEl = target.closest(
          "[data-input-zone]"
        ) as HTMLElement | null;
        if (zoneEl) {
          const gId = zoneEl.dataset.inputZone ?? "";
          if (gId === "") return;
          const count = Number(zoneEl.dataset.inputCount ?? 0);
          setSidebarOverInput((prev) =>
            prev?.groupId === gId && prev?.index === count
              ? prev
              : {
                  groupId: gId,
                  index: count,
                  mode: "insert",
                  targetIndex: undefined,
                  mergeCandidateIndex: undefined,
                }
          );
          return;
        }
        setSidebarOverInput(null);
      };

      const onUp = (upEvt: PointerEvent) => {
        document.removeEventListener("pointermove", onMove);
        document.removeEventListener("pointerup", onUp);

        const draggedArg = draggingSidebarArg.current;
        draggingSidebarArg.current = null;
        setSidebarDragState(null);
        setSidebarOverInput(null);

        if (!draggedArg || typeof draggedArg !== "object") return;

        const target = resolveTarget(upEvt.clientX, upEvt.clientY);
        if (!target) return;

        let targetGroupId: string | null = null;
        let insertIndex: number | null = null;
        let mergeTargetIndex: number | null = null;

        const inputEl = target.closest(
          "[data-input-item]"
        ) as HTMLElement | null;
        if (inputEl) {
          const gId = inputEl.dataset.groupId ?? "";
          targetGroupId = gId === "" ? null : gId;
          const idx = Number(inputEl.dataset.inputItem ?? 0);
          const itemRect = inputEl.getBoundingClientRect();
          const relativeY = upEvt.clientY - itemRect.top;
          const topThirdEnd = itemRect.height * 0.33;
          const bottomThirdStart = itemRect.height * 0.67;
          insertIndex =
            relativeY < topThirdEnd
              ? idx
              : relativeY > bottomThirdStart
              ? idx + 1
              : idx;

          const allGroups = solution?.ComponentConfig?.steps
            ?.flatMap((step: any) => step.inputGroups || [])
            ?.filter(Boolean);
          const targetInput = allGroups?.find(
            (group: any) => group.id === targetGroupId
          )?.inputs?.[idx];
          const canMerge = canSidebarMergeWithInput(
            draggedArg,
            targetInput,
            ComponentUuid
          );
          if (
            canMerge &&
            relativeY >= topThirdEnd &&
            relativeY <= bottomThirdStart
          ) {
            mergeTargetIndex = idx;
          }
        } else {
          const zoneEl = target.closest(
            "[data-input-zone]"
          ) as HTMLElement | null;
          if (zoneEl) {
            const gId = zoneEl.dataset.inputZone ?? "";
            targetGroupId = gId === "" ? null : gId;
            insertIndex = Number(zoneEl.dataset.inputCount ?? 0);
          }
        }

        if (!targetGroupId) return;

        setSolution?.((prev: SolutionType) => {
          if (!prev?.ComponentConfig?.steps) return prev;
          return {
            ...prev,
            ComponentConfig: {
              ...prev.ComponentConfig,
              steps: prev.ComponentConfig.steps.map((step: any) => ({
                ...step,
                inputGroups: step.inputGroups.map((group: any) => {
                  if (group.id !== targetGroupId) return group;
                  const newInputs = [...group.inputs];

                  if (
                    mergeTargetIndex !== null &&
                    mergeTargetIndex >= 0 &&
                    mergeTargetIndex < newInputs.length
                  ) {
                    const targetInput = newInputs[mergeTargetIndex];
                    const sourceRef = getSidebarMergeRef(
                      draggedArg,
                      ComponentUuid
                    );
                    if (!sourceRef) return { ...group, inputs: newInputs };
                    const mergedRefs = [
                      ...(targetInput?.MergedInputRefs || []),
                    ].filter(
                      (ref: any) =>
                        !(
                          ref?.ComponentUuid === sourceRef.ComponentUuid &&
                          ref?.Field === sourceRef.Field
                        )
                    );
                    mergedRefs.push(sourceRef);

                    newInputs[mergeTargetIndex] = {
                      ...targetInput,
                      MergedInputRefs: mergedRefs,
                    };

                    return { ...group, inputs: newInputs };
                  }

                  if (insertIndex !== null && insertIndex < newInputs.length) {
                    newInputs.splice(
                      insertIndex,
                      0,
                      ComponentUuid
                        ? { ...draggedArg, ComponentUuid }
                        : { ...draggedArg }
                    );
                  } else {
                    newInputs.push(
                      ComponentUuid
                        ? { ...draggedArg, ComponentUuid }
                        : { ...draggedArg }
                    );
                  }
                  return { ...group, inputs: newInputs };
                }),
              })),
            },
          };
        });
      };

      document.addEventListener("pointermove", onMove);
      document.addEventListener("pointerup", onUp);
    },
    [
      canSidebarMergeWithInput,
      getSidebarMergeRef,
      setSolution,
      solution?.ComponentConfig?.steps,
    ]
  );

  const getExternalFieldLabel = useCallback(
    (fieldType: string, key: string, value: any) => {
      if (fieldType === "DataStructure") {
        const sourceDataStructureKeys = new Set<string>([key]);
        [...(importedJSON?.Components || []), ...(solution?.Components || [])].forEach(
          (component: any) => {
            const sourceKey = component?.Bag?.External?.DataStructure?.[key];
            if (sourceKey) sourceDataStructureKeys.add(String(sourceKey));
          }
        );
        const inputArguments = [
          ...(importedJSON?.Item?.InputArguments || []),
          ...(importedJSON?.Components || []).flatMap(
            (component: any) => component?.Item?.InputArguments || []
          ),
          ...(solution?.Components || []).flatMap(
            (component: any) => component?.Item?.InputArguments || []
          ),
        ];
        const matchingArgument = inputArguments.find(
          (argument: any) => sourceDataStructureKeys.has(argument.DataStructure)
        );

        return (
          matchingArgument?.Label ||
          matchingArgument?.Field ||
          matchingArgument?.Name ||
          key
        );
      }

      try {
        const parsed = JSON.parse(key);
        if (parsed?.label) return parsed.label;
        if (parsed?.Label) return parsed.Label;
        if (parsed?.field?.label && parsed?.option?.label) {
          return `${parsed.field.label} / ${parsed.option.label}`;
        }
        if (parsed?.option?.label) return parsed.option.label;
        if (parsed?.field?.label) return parsed.field.label;
      } catch (error) {
        // Some keys are plain UUIDs and not JSON strings.
      }

      if (value && typeof value === "object") {
        if (value.label) return value.label;
        if (value.Label) return value.Label;
      }

      return key;
    },
    [
      importedJSON?.Components,
      importedJSON?.Item?.InputArguments,
      solution?.Components,
    ]
  );

  const shouldShowExternalInputType = useCallback(
    (fieldType: string) =>
      fieldType !== "DataStructureField" &&
      fieldType !== "ReportingTemplateField",
    []
  );

  const toExternalDragArg = useCallback(
    (fieldType: string, fieldKey: string, fieldValue: any) => ({
      __kind: "external",
      fieldType,
      fieldKey,
      fieldValue,
      UUID: `external:${fieldType}:${fieldKey}`,
      Label: getExternalFieldLabel(fieldType, fieldKey, fieldValue),
      Description: `External ${fieldType}`,
    }),
    [getExternalFieldLabel]
  );

  const toComponentConfigDragArgs = useCallback((component: any) => {
    if (
      component.Role === "weather-monitor" ||
      component.Role === "ping-monitor"
    ) {
      return [
        {
          __kind: "component-config",
          configType: component.Role,
          componentUuid: component.ComponentUuid,
          UUID: `component-config:${component.ComponentUuid}:monitor`,
          Label: "Monitor Config",
          Description: component.Item?.Label,
        },
      ];
    }

    if (component.Role === "inbound-api") {
      return [
        {
          __kind: "component-config",
          configType: "inbound-api",
          componentUuid: component.ComponentUuid,
          UUID: `component-config:${component.ComponentUuid}:inbound-api`,
          Label: "API Connection",
          Description: component.Item?.Label,
        },
      ];
    }

    if (component.Role === "inbound-message") {
      return [
        {
          __kind: "component-config",
          configType: "inbound-message",
          componentUuid: component.ComponentUuid,
          UUID: `component-config:${component.ComponentUuid}:inbound-message`,
          Label: "Inbound Channel",
          Description: component.Item?.Label,
        },
      ];
    }

    return [];
  }, []);

  const handleAddAll = useCallback(() => {
    if (!setSolution) return;

    setSolution((prev: SolutionType) => {
      const buildStep = (
        name: string,
        description: string,
        inputGroups: any[]
      ) => ({
        name,
        description,
        inputGroups,
      });

      const buildInputGroup = (
        id: string,
        name: string,
        description: string,
        inputs: any[]
      ) => ({
        id,
        name,
        description,
        inputs,
      });

      const sortByOrder = (items: any[]) =>
        [...items].sort((a: any, b: any) => (a?.Order ?? 0) - (b?.Order ?? 0));

      const existingSteps = (prev?.ComponentConfig?.steps || []).map(
        (step: any) => ({
          ...step,
          inputGroups: (step.inputGroups || []).map((group: any) => ({
            ...group,
            inputs: [...(group.inputs || [])],
          })),
        })
      );

      const usedUuids = new Set<string>(
        existingSteps.flatMap((step: any) =>
          (step.inputGroups || []).flatMap((group: any) =>
            (group.inputs || []).map((input: any) => input?.UUID)
          )
        )
      );

      const registerArgs = (args: any[]) =>
        args
          .map((arg, index) => {
            const normalizedUuid =
              arg?.UUID ||
              `component-input:${arg?.ComponentUuid || "arg"}:${
                arg?.Field || arg?.Label || index
              }`;
            return {
              ...arg,
              UUID: normalizedUuid,
              Label:
                arg?.Label || arg?.Field || arg?.Name || `Input ${index + 1}`,
              Description: arg?.Description || "",
            };
          })
          .filter((arg) => {
            if (usedUuids.has(arg.UUID)) return false;
            usedUuids.add(arg.UUID);
            return true;
          });

      const groupInputsByVariableGroups = (
        component: any,
        args: any[],
        groupPrefix: string,
        emptyGroupLabel: string
      ) => {
        const variableGroups = [...(component.Item?.VariableGroups || [])].sort(
          (a: any, b: any) => (a?.Order ?? 0) - (b?.Order ?? 0)
        );

        const grouped = args.reduce((acc: Record<string, any[]>, arg: any) => {
          const groupUuid = arg?.GroupUuid || "";
          if (!acc[groupUuid]) acc[groupUuid] = [];
          acc[groupUuid].push(arg);
          return acc;
        }, {});

        const groups = variableGroups.map((group: any, index: number) => {
          const groupInputs = sortByOrder(grouped[group.GroupUuid] || []);
          return buildInputGroup(
            `${groupPrefix}:${group.GroupUuid || index}`,
            group.Label || emptyGroupLabel,
            group.Description ||
              component.Item?.Description ||
              component.Description ||
              "",
            groupInputs
          );
        });

        const defaultGroupInputs = sortByOrder(grouped[""] || []);
        if (variableGroups.length === 0 && args.length > 0) {
          groups.push(
            buildInputGroup(
              `${groupPrefix}:default`,
              "Default Group",
              component.Item?.Description || component.Description || "",
              defaultGroupInputs.length > 0
                ? defaultGroupInputs
                : sortByOrder(args)
            )
          );
        }

        return groups;
      };

      const externalArgs = registerArgs(
        Object.entries(importedJSON?.Bag?.External || {}).flatMap(
          ([fieldType, fields]) =>
            shouldShowExternalInputType(fieldType)
              ? Object.entries(fields || {}).map(([fieldKey, fieldValue]) =>
                  toExternalDragArg(fieldType, fieldKey, fieldValue)
                )
              : []
        )
      );

      const externalStep = buildStep(
        "External Inputs",
        "Imported external fields.",
        externalArgs.length
          ? [
              buildInputGroup(
                "external-inputs",
                "External Fields",
                "External fields from imported JSON.",
                sortByOrder(externalArgs)
              ),
            ]
          : []
      );

      const componentConfigGroups = (solution?.Components || []).flatMap(
        (component: any, componentIndex: number) => {
          const configArgs = registerArgs(
            toComponentConfigDragArgs(component).map(
              (arg: any, index: number) => ({
                ...arg,
                ComponentUuid: component.ComponentUuid,
                UUID: `component-config:${component.ComponentUuid}:${
                  component.Role
                }:${arg?.configType || index}`,
              })
            )
          );

          if (configArgs.length === 0) return [];

          return [
            buildInputGroup(
              `component-config:${component.ComponentUuid}`,
              component.Item?.Label ||
                component.Role ||
                `Component ${componentIndex + 1}`,
              component.Item?.Description ||
                component.Description ||
                "Configure component options.",
              sortByOrder(configArgs)
            ),
          ];
        }
      );

      const componentSteps = (solution?.Components || []).flatMap(
        (component: any, componentIndex: number) => {
          const parameterArgs = registerArgs(
            (component.Item?.InputArguments || [])
              .filter(
                (arg: any) => arg.IsParameter && !arg.SolutionDoNotConfigure
              )
              .map((arg: any, index: number) => ({
                ...arg,
                ComponentUuid: component.ComponentUuid,
                UUID: `component-input:${component.ComponentUuid}:${
                  component.Role
                }:${arg?.Field || arg?.Label || arg?.UUID || index}`,
              }))
          );

          if (
            parameterArgs.length === 0 &&
            (component.Item?.VariableGroups || []).length === 0
          ) {
            return [];
          }

          const componentLabel =
            component.Item?.Label ||
            component.Role ||
            `Component ${componentIndex + 1}`;
          const componentDescription =
            component.Item?.Description ||
            component.Description ||
            "Configure component inputs.";

          return groupInputsByVariableGroups(
            component,
            parameterArgs,
            `component-step:${component.ComponentUuid}:${component.Role}`,
            "Input Group"
          )
            .filter((group: any) => (group.inputs || []).length > 0)
            .map((group: any) =>
              buildStep(
                group.name && group.name !== "Default Group"
                  ? `${componentLabel}: ${group.name}`
                  : componentLabel,
                group.description || componentDescription,
                [group]
              )
            );
        }
      );

      const ensureStep = (
        matcher: (step: any) => boolean,
        factory: () => any
      ) => {
        let index = existingSteps.findIndex(matcher);
        if (index === -1) {
          const created = factory();
          existingSteps.push(created);
          index = existingSteps.length - 1;
        }

        if (!existingSteps[index].inputGroups) {
          existingSteps[index].inputGroups = [];
        }

        return existingSteps[index];
      };

      const upsertGroupInputs = (step: any, groupTemplate: any) => {
        if (!step.inputGroups) step.inputGroups = [];

        const groupIndex = step.inputGroups.findIndex(
          (group: any) => group.id === groupTemplate.id
        );

        if (groupIndex === -1) {
          if ((groupTemplate.inputs || []).length === 0) return;
          step.inputGroups.push({
            ...groupTemplate,
            inputs: [...(groupTemplate.inputs || [])],
          });
          return;
        }

        const existingGroup = step.inputGroups[groupIndex];
        const templateInputByUuid = new Map(
          (groupTemplate.inputs || []).map((input: any) => [input.UUID, input])
        );
        existingGroup.inputs = (existingGroup.inputs || []).map((input: any) => {
          const templateInput: any = templateInputByUuid.get(input.UUID);
          if (!templateInput) return input;

          return {
            ...input,
            Label: templateInput.Label || input.Label,
            Description: templateInput.Description || input.Description,
          };
        });

        const existingInputUuids = new Set(
          (existingGroup.inputs || []).map((input: any) => input.UUID)
        );
        const additions = (groupTemplate.inputs || []).filter(
          (input: any) => !existingInputUuids.has(input.UUID)
        );

        if (additions.length) {
          existingGroup.inputs = [
            ...(existingGroup.inputs || []),
            ...additions,
          ];
        }
      };

      const externalStepTarget = ensureStep(
        (step: any) =>
          step?.name === "External Inputs" ||
          step?.inputGroups?.some(
            (group: any) => group.id === "external-inputs"
          ),
        () => buildStep("External Inputs", "Imported external fields.", [])
      );

      (externalStep.inputGroups || []).forEach((group: any) => {
        upsertGroupInputs(externalStepTarget, group);
      });

      const componentConfigStepTarget = ensureStep(
        (step: any) =>
          step?.name === "Component Configuration" ||
          step?.inputGroups?.some((group: any) =>
            String(group?.id || "").startsWith("component-config:")
          ),
        () =>
          buildStep(
            "Component Configuration",
            "Configure monitor, API, and inbound message options.",
            []
          )
      );

      componentConfigGroups.forEach((group: any) => {
        upsertGroupInputs(componentConfigStepTarget, group);
      });

      componentSteps.forEach((componentStepTemplate: any) => {
        const groupIds = (componentStepTemplate.inputGroups || []).map(
          (group: any) => group.id
        );

        const componentStepTarget = ensureStep(
          (step: any) =>
            step?.inputGroups?.some((group: any) =>
              groupIds.includes(group.id)
            ),
          () =>
            buildStep(
              componentStepTemplate.name,
              componentStepTemplate.description,
              []
            )
        );

        (componentStepTemplate.inputGroups || []).forEach((group: any) => {
          upsertGroupInputs(componentStepTarget, group);
        });
      });

      return {
        ...prev,
        ComponentConfig: {
          ...prev.ComponentConfig,
          steps: existingSteps,
        },
      };
    });
  }, [
    importedJSON?.Bag?.External,
    setSolution,
    shouldShowExternalInputType,
    solution?.Components,
    toExternalDragArg,
    toComponentConfigDragArgs,
  ]);

  const handleResetAll = useCallback(() => {
    if (!setSolution) return;

    setSolution((prev: SolutionType) => ({
      ...prev,
      ComponentConfig: {
        ...prev.ComponentConfig,
        steps: [],
      },
    }));
    setActiveStepIndex(0);
    setFocusedStepIndex(null);
    setFocusedInputGroupId(null);
    setFocusedInputNodeKey(null);
    setFocusedLogicNodeKey(null);
    setPendingFlowSelection(null);
    setDiagramInputModal(null);
  }, [setSolution]);

  const usedInputs =
    solution?.ComponentConfig?.steps?.flatMap((step: any) =>
      step.inputGroups?.flatMap((group: any) => group.inputs)
    ) || [];

  const inputUsageByKey = React.useMemo<
    Record<string, SolutionInputUsage[]>
  >(() => {
    const componentInputTokensBySyntheticToken = new Map<string, string[]>();

    (solution?.Components || []).forEach((component: any) => {
      const componentUuid = stringifySetupValue(
        component?.ComponentUuid || component?.ItemUuid
      );

      (component?.Item?.InputArguments || []).forEach(
        (argument: any, argumentIndex: number) => {
          const syntheticUuid = stringifySetupValue(
            `component-input:${componentUuid}:${component?.Role}:${
              argument?.Field ||
              argument?.Label ||
              argument?.UUID ||
              argumentIndex
            }`
          ).toLowerCase();
          const tokens = getSetupInputArgumentTokens(argument, componentUuid);

          if (syntheticUuid && tokens.length) {
            componentInputTokensBySyntheticToken.set(syntheticUuid, tokens);
          }
        }
      );
    });

    const inputTokenGroups = usedInputs
      .map((input: any) => {
        const componentUuids = getSetupInputComponentUuids(input);
        const inputTokens = getSetupInputTokens(input);
        const lookupTokens = getSetupInputLookupTokens(input);
        const componentArgumentTokens = inputTokens.flatMap(
          (token) => componentInputTokensBySyntheticToken.get(token) || []
        );
        const tokens = normalizeSetupTokens([
          ...inputTokens,
          ...componentArgumentTokens,
        ]).filter((token) => token.length > 1);

        return { componentUuids, lookupTokens, tokens };
      })
      .filter(
        ({ lookupTokens, tokens }) => lookupTokens.length && tokens.length
      );
    const usageByToken = new Map<string, SolutionInputUsage[]>();
    const seenUsageKeysByToken = new Map<string, Set<string>>();

    const addUsage = (token: string, usage: SolutionInputUsage) => {
      const seenUsageKeys =
        seenUsageKeysByToken.get(token) || new Set<string>();
      if (seenUsageKeys.has(usage.key)) return;

      seenUsageKeys.add(usage.key);
      seenUsageKeysByToken.set(token, seenUsageKeys);
      usageByToken.set(token, [...(usageByToken.get(token) || []), usage]);
    };

    if (!solution || !inputTokenGroups.length) return {};

    const seenListenerConditionUsageKeys = new Set<string>();

    getSolutionComponentLogicBranches(solution).forEach((branch: any) => {
      const component = branch.component;
      const componentUuid = stringifySetupValue(
        component?.ComponentUuid || component?.ItemUuid
      );
      const componentLabel =
        stringifySetupValue(
          component?.Item?.Label || component?.Label || component?.Role
        ) || `Component ${branch.componentIndex + 1}`;
      const workflowLabel = branch.workflow
        ? stringifySetupValue(
            branch.workflow?.Label ||
              branch.workflow?.Name ||
              branch.workflow?.UUID ||
              `Workflow ${(branch.workflowIndex ?? 0) + 1}`
          )
        : undefined;
      const eventLabel = getSetupHumanLabel(
        branch.eventType || branch.listener?.EventType
      );
      const ruleLabel = stringifySetupValue(
        branch.rule?.Label ||
          branch.rule?.Name ||
          `Rule ${branch.ruleIndex + 1}`
      );
      const listenerLabel = stringifySetupValue(
        branch.listener?.Label ||
          branch.listener?.Name ||
          branch.eventHandler?.Label ||
          branch.eventHandler?.Name ||
          `Listener ${branch.listenerIndex + 1}`
      );

      const addSourceUsage = (
        source: any,
        kind: "Condition" | "Action",
        label: string,
        sourceIndex: number
      ) => {
        const logicNodeKey = [
          kind === "Condition" ? "condition" : "action",
          componentUuid || branch.componentIndex || "component",
          branch.workflowKey ?? branch.workflowIndex ?? "workflow",
          branch.listener?.UUID ?? branch.listenerIndex ?? "listener",
          branch.rule?.UUID ?? branch.ruleIndex ?? "rule",
          kind === "Action" ? source?.UUID || sourceIndex : "decision",
        ].join(":");
        const valueStrings = getSetupLogicValueStrings(source);
        const matchedInputTokenGroups = inputTokenGroups.filter(
          ({ componentUuids, tokens }) => {
            if (
              componentUuid &&
              componentUuids.length > 0 &&
              !componentUuids.includes(componentUuid.toLowerCase())
            ) {
              return false;
            }

            return tokens.some((token) =>
              valueStrings.some(
                (value) => value === token || value.includes(token)
              )
            );
          }
        );

        matchedInputTokenGroups.forEach(({ lookupTokens }) => {
          const usage = {
            key: [
              kind,
              componentUuid,
              branch.workflowKey ?? branch.workflowIndex ?? "workflow",
              branch.listener?.UUID ?? branch.listenerIndex ?? "listener",
              branch.rule?.UUID ?? branch.ruleIndex ?? "rule",
              source?.UUID || sourceIndex,
            ].join(":"),
            kind,
            label,
            logicNodeKey,
            componentLabel,
            workflowLabel,
            eventLabel,
            ruleLabel,
          };

          lookupTokens.forEach((token) => addUsage(token, usage));
        });
      };

      getSetupRuleConditions(branch.rule).forEach(
        (condition: any, conditionIndex: number) => {
          addSourceUsage(
            condition,
            "Condition",
            getSetupHumanLabel(getSetupConditionTypeKey(condition)) ||
              `Condition ${conditionIndex + 1}`,
            conditionIndex
          );
        }
      );

      const listenerCondition = getSetupListenerCondition(branch.listener);

      if (listenerCondition) {
        const listenerConditionUsageKey = [
          componentUuid,
          branch.workflowKey ?? branch.workflowIndex ?? "workflow",
          branch.listener?.UUID ?? branch.listenerIndex ?? "listener",
          listenerCondition.UUID ?? "listener-condition",
        ].join(":");

        if (!seenListenerConditionUsageKeys.has(listenerConditionUsageKey)) {
          seenListenerConditionUsageKeys.add(listenerConditionUsageKey);
          addSourceUsage(
            listenerCondition,
            "Condition",
            `Listener condition - ${listenerLabel}`,
            -1
          );
        }
      }

      getSetupRuleActions(branch.rule).forEach(
        (action: any, actionIndex: number) => {
          addSourceUsage(
            action,
            "Action",
            getSetupHumanLabel(getSetupActionTypeKey(action)) ||
              `Action ${actionIndex + 1}`,
            actionIndex
          );
        }
      );
    });

    return Array.from(usageByToken.entries()).reduce(
      (acc, [token, usages]) => ({ ...acc, [token]: usages }),
      {}
    );
  }, [solution, usedInputs]);

  const flattenedFormData = Object.values(formData).reduce(
    (acc, val) => ({ ...acc, ...val }),
    {}
  );
  const getIsVisible = (vg: any) =>
    !vg.VisibilityField ||
    usedInputs.some(
      (arg: any) =>
        arg.Field === vg.VisibilityField &&
        ((vg.VisibilityLogical === "is" &&
          (vg.VisibilityValues ?? []).includes(
            flattenedFormData?.[arg.Field]?.[0] ?? ""
          )) ||
          (vg.VisibilityLogical === "is_not" &&
            !(vg.VisibilityValues ?? []).includes(
              flattenedFormData?.[arg.Field]?.[0] ?? ""
            )) ||
          (vg.VisibilityLogical === "gte" &&
            flattenedFormData?.[arg.Field]?.some((v: any) =>
              (vg.VisibilityValues ?? []).some(
                (value: number) => (value as number) <= parseFloat(v)
              )
            )) ||
          (vg.VisibilityLogical === "lte" &&
            flattenedFormData?.[arg.Field]?.some((v: any) =>
              (vg.VisibilityValues ?? []).some(
                (value: number) => (value as number) >= parseFloat(v)
              )
            )))
    );

  const visibleSteps = React.useMemo(() => {
    const steps = solution?.ComponentConfig?.steps || [];

    return steps
      .map((step: any, originalIndex: number) => ({ step, originalIndex }))
      .filter(({ step }: any) => {
        if (disableInputDragging && getIsVisible && !getIsVisible(step)) {
          return false;
        }

        if (!isUserMode) return true;

        return (step.inputGroups || []).some((inputGroup: any) => {
          if (disableInputDragging && getIsVisible && !getIsVisible(inputGroup)) {
            return false;
          }

          return (inputGroup.inputs || []).some((input: any) => {
            if (!input || input.MergedIntoRef) return false;

            if (disableInputDragging && getIsVisible && !getIsVisible(input)) {
              return false;
            }

            if (
              showDefaultValueInputsOnly &&
              (input.__kind === "external" || input.__kind === "component-config")
            ) {
              return false;
            }

            if (
              input.__kind === "external" &&
              !shouldShowExternalInputType(input.fieldType)
            ) {
              return false;
            }

            return true;
          });
        });
      });
  }, [
    disableInputDragging,
    flattenedFormData,
    isUserMode,
    shouldShowExternalInputType,
    showDefaultValueInputsOnly,
    solution?.ComponentConfig?.steps,
    usedInputs,
  ]);

  useEffect(() => {
    if (!visibleSteps.length) return;

    if (
      !visibleSteps.some(
        ({ originalIndex }: any) => originalIndex === activeStepIndex
      )
    ) {
      setActiveStepIndex(visibleSteps[0].originalIndex);
    }
  }, [activeStepIndex, visibleSteps]);

  const diagramInputStep =
    diagramInputModal &&
    solution?.ComponentConfig?.steps?.[diagramInputModal.stepIndex];
  const diagramInputGroup = diagramInputStep?.inputGroups?.find(
    (group: any) => group.id === diagramInputModal?.groupId
  );
  const diagramInput =
    typeof diagramInputModal?.inputIndex === "number"
      ? diagramInputGroup?.inputs?.[diagramInputModal.inputIndex]
      : undefined;

  return (
    <div className="">
      {/* Sidebar arg ghost — portalled to body so it's never clipped */}
      {sidebarDragState &&
        ReactDOM.createPortal(
          <div
            ref={(el: HTMLDivElement | null) => {
              sidebarGhostEl.current = el;
            }}
            style={{
              position: "fixed",
              top: sidebarDragState.top,
              left: sidebarDragState.left,
              width: sidebarDragState.width,
              minWidth: 400,
              pointerEvents: "none",
              zIndex: 9999,
              opacity: 0.85,
              background: "white",
              border: "2px dashed #6c5ce7",
              borderRadius: 8,
              padding: "12px",
              boxShadow: "0 8px 24px rgba(0,0,0,0.18)",
            }}
          >
            {sidebarDragState.arg?.__kind === "external" ? (
              <SolutionImportInput
                previewOnly
                fieldType={sidebarDragState.arg.fieldType}
                fieldKey={sidebarDragState.arg.fieldKey}
                value={sidebarDragState.arg.fieldValue}
                previewLabel={sidebarDragState.arg.Label}
              />
            ) : sidebarDragState.arg?.__kind === "component-config" ? (
              <div className="rounded px-2 py-1 w-100 text-center btn btn-light">
                <div>{sidebarDragState.arg.Label}</div>
                <div className="text-secondary text-small">
                  {sidebarDragState.arg.Description}
                </div>
              </div>
            ) : (
              <>
                <div className="d-flex align-items-center gap-2 text-sm mb-1 fw-semibold">
                  {sidebarDragState.arg.Label}
                </div>
                {sidebarDragState.arg.Description && (
                  <div className="text-secondary text-sm mb-2">
                    {sidebarDragState.arg.Description}
                  </div>
                )}
                <InputArgumentField
                  arg={sidebarDragState.arg}
                  values={[]}
                  onChange={() => {}}
                  inputArgumentsInEditor={[]}
                />
              </>
            )}
          </div>,
          document.body
        )}
      {navDragGhost &&
        ReactDOM.createPortal(
          <div
            style={{
              position: "fixed",
              top: navDragGhost.top,
              left: navDragGhost.left,
              width: navDragGhost.width,
              minHeight: navDragGhost.height,
              pointerEvents: "none",
              zIndex: 10001,
              opacity: 0.88,
              background: "white",
              border: "2px dashed #6c5ce7",
              borderRadius: 8,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              padding: "8px 12px",
              boxShadow: "0 8px 24px rgba(0,0,0,0.18)",
              fontWeight: 700,
            }}
          >
            {navDragGhost.label}
          </div>,
          document.body
        )}
      <div className="p-3 bg-white">
        <div className="overflow-x-auto">
          <div className="d-flex align-items-center mt-3 mt-lg-0">
            <div className="border-end me-2">
              <MyIcon Icon={SolutionIcon} width={50} className="m-2" />
            </div>
            <div className="" style={{ minWidth: 300 }}>
              <div className="fs-3">Solutions</div>
              <div className="fs-3">Builder Progress</div>
              <div className="text-secondary">
                Lorem ipsum dolor sit amet consectetur.
              </div>
            </div>
            <Nav
              variant="tabs"
              className="mb-3 flex-nowrap justify-content-center justify-content-lg-start gap-2 border-0"
            >
              {visibleSteps.map(
                ({ step, originalIndex }: any, visibleIndex: number) => {
                  const stepKey =
                    step.inputGroups?.[0]?.id ||
                    `${step.name}-${step.description}-${originalIndex}`;

                  return (
                    <Nav.Item
                      key={stepKey}
                      draggable={!!setSolution}
                      onDragStart={(e) =>
                        handleNavStepDragStart(
                          e,
                          originalIndex,
                          step.name || `Step ${originalIndex + 1}`,
                          150,
                          96
                        )
                      }
                      onDragOver={(e) =>
                        handleNavStepDragOver(e, originalIndex)
                      }
                      onDrop={() =>
                        handleNavStepDrop(
                          originalIndex,
                          navDropRegion?.index === originalIndex
                            ? navDropRegion.side
                            : "right"
                        )
                      }
                      onDragEnd={resetNavDragState}
                      style={{ position: "relative" }}
                    >
                      {navDropRegion?.index === originalIndex &&
                        navDragFromIndex !== null && (
                          <>
                            <div
                              style={{
                                position: "absolute",
                                top: 4,
                                bottom: 4,
                                left: -2,
                                width: 4,
                                borderRadius: 99,
                                background:
                                  navDropRegion.side === "left"
                                    ? "#6c5ce7"
                                    : "transparent",
                                zIndex: 2,
                                pointerEvents: "none",
                              }}
                            />
                            <div
                              style={{
                                position: "absolute",
                                top: 4,
                                bottom: 4,
                                right: -2,
                                width: 4,
                                borderRadius: 99,
                                background:
                                  navDropRegion.side === "right"
                                    ? "#6c5ce7"
                                    : "transparent",
                                zIndex: 2,
                                pointerEvents: "none",
                              }}
                            />
                          </>
                        )}
                      <Nav.Link
                        onClick={() => focusStep(originalIndex)}
                        className="border-0 h-100"
                      >
                        <div
                          style={{ width: 150 }}
                          className={`border rounded p-2 px-3 position-relative d-flex flex-column h-100 ${
                            activeStepIndex === originalIndex
                              ? "border-purple shadow-purple"
                              : ""
                          } ${
                            navDropRegion?.index === originalIndex &&
                            navDragFromIndex !== null &&
                            navDragFromIndex !== originalIndex
                              ? "border-purple"
                              : ""
                          } align-items-center gap-1`}
                        >
                          <div className="badge bg-colored position-absolute top-0 start-0 translate-middle-x mt-2">
                            {visibleIndex + 1}
                          </div>
                          <div className="">
                            <MyIcon Icon={SolutionIcon} width={20} />
                          </div>
                          <div className="text-navy fw-bold text-uppercase text-small text-nowrap">
                            Step {visibleIndex + 1}
                          </div>
                          <div className="fw-bold">{step.name}</div>
                        </div>
                      </Nav.Link>
                    </Nav.Item>
                  );
                }
              )}
              {setSolution && solution && (
                <Nav.Item>
                  <Nav.Link
                    onClick={() => {
                      const newIndex =
                        solution?.ComponentConfig?.steps?.length ?? 0;
                      setSolution({
                        ...solution,
                        ComponentConfig: {
                          ...solution.ComponentConfig,
                          steps: [
                            ...(solution.ComponentConfig?.steps || []),
                            {
                              name: `Step ${newIndex + 1}`,
                              description:
                                "Step description goes here. Lorem ipsum dolor sit amet consectetur adipisicing elit.",
                              inputGroups: [
                                {
                                  id: `group-${Date.now()}`,
                                  name: "Input Group 1",
                                  description:
                                    "Input group description goes here. Start by dragging input elements from the left panel.",
                                  inputs: [],
                                },
                              ],
                            },
                          ],
                        },
                      });
                      focusStep(newIndex);
                    }}
                    className="border-0 h-100"
                  >
                    <div
                      style={{ width: 150 }}
                      className={`border rounded p-2 px-3 position-relative d-flex flex-column h-100 align-items-center gap-1`}
                    >
                      <div className="badge bg-colored position-absolute top-0 start-0 translate-middle-x mt-2"></div>
                      <div className="">
                        {/* <MyIcon Icon={step.icon} width={20} /> */}
                      </div>
                      <div className="text-navy fw-bold text-uppercase text-small text-nowrap"></div>
                      <div className="fw-bold">Add step</div>
                    </div>
                  </Nav.Link>
                </Nav.Item>
              )}
            </Nav>
          </div>
        </div>
      </div>
      <div className="d-flex align-items-center justify-content-between gap-2 mt-3">
        <div className="d-flex align-items-center gap-2 flex-wrap">
          {setSolution && (
            <>
              <button
                type="button"
                className="btn btn-colored"
                onClick={handleAddAll}
              >
                Add All
              </button>
              <button
                type="button"
                className="btn btn-outline-secondary"
                onClick={handleResetAll}
              >
                Reset
              </button>
            </>
          )}
          {isAdminMode && (
            <div className="d-flex align-items-center gap-1 ms-3">
              <Form.Check
                type="switch"
                className="flex-row-reverse m-0"
                label={
                  <div
                    className="me-2"
                    role="button"
                    aria-disabled={!isAiAssistantEnabled}
                    style={
                      isAiAssistantEnabled
                        ? undefined
                        : {
                            color: "#9ca3af",
                            cursor: "not-allowed",
                            opacity: 0.65,
                          }
                    }
                    onClick={(event) => {
                      event.preventDefault();
                      event.stopPropagation();
                      if (!isAiAssistantEnabled) return;
                      handleOpenAiAssistantSidebar();
                    }}
                  >
                    {isAiAssistantEnabled &&
                    (isAiAssistantLoading || aiAssistantSuggestions.length > 0) ? (
                      <span className="d-inline-block position-relative">
                        <AiIcon width={30} className="" />
                        {isAiAssistantLoading ? (
                          <span className="position-absolute top-0 start-100 translate-middle">
                            <span
                              className="spinner-border spinner-border-sm text-primary bg-white rounded-circle"
                              role="status"
                              aria-hidden="true"
                            />
                          </span>
                        ) : (
                          <span className="badge bg-colored rounded-pill position-absolute top-0 start-100 translate-middle">
                            {aiAssistantSuggestions.length}
                          </span>
                        )}
                      </span>
                    ) : (
                      <span className="d-inline-block">
                        <AiIcon
                          width={30}
                          className=""
                          style={
                            isAiAssistantEnabled
                              ? undefined
                              : { filter: "grayscale(1)", opacity: 0.55 }
                          }
                        />
                      </span>
                    )}
                    <span
                      className={
                        isAiAssistantEnabled
                          ? "text-navy fw-bold me-1"
                          : "text-muted fw-bold me-1"
                      }
                    >
                      AI
                    </span>
                    <span>Assistant</span>
                  </div>
                }
                id="ai-switch"
                checked={isAiAssistantEnabled}
                onChange={(event) =>
                  handleAiAssistantToggle(event.currentTarget.checked)
                }
              />
            </div>
          )}
        </div>
      </div>
      <div className="row mt-3" style={{ height: "calc(100vh - 250px)" }}>
        {setSolution && (
          <div
            className="col-2"
            style={{ maxHeight: "100%", overflowY: "auto" }}
          >
            {solution?.Components?.map((component) => {
              const componentArgs = component.Item.InputArguments.filter(
                (arg) => arg.IsParameter && !arg.SolutionDoNotConfigure
              ).filter((arg) => {
                // Only show arguments that are not already used in the solution steps
                const isUsed = solution?.ComponentConfig?.steps?.some(
                  (step: any) =>
                    step.inputGroups.some((group: any) =>
                      group.inputs.some((input: any) => {
                        const directMatch =
                          input.ComponentUuid === component.ComponentUuid &&
                          ((arg.UUID && input.UUID === arg.UUID) ||
                            (arg.Field && input.Field === arg.Field));

                        const mergedRefMatch =
                          Array.isArray(input.MergedInputRefs) &&
                          !!arg.Field &&
                          input.MergedInputRefs.some(
                            (ref: any) =>
                              ref?.ComponentUuid === component.ComponentUuid &&
                              ref?.Field === arg.Field
                          );

                        return directMatch || mergedRefMatch;
                      })
                    )
                );
                return !isUsed;
              });
              const componentVariableGroups = [
                ...(component.Item?.VariableGroups || []),
              ].sort((a: any, b: any) => (a?.Order ?? 0) - (b?.Order ?? 0));
              const groupLabelByUuid = new Map(
                componentVariableGroups.map((group: any) => [
                  group.GroupUuid,
                  group.Label,
                ])
              );
              if (!groupLabelByUuid.has("")) {
                groupLabelByUuid.set("", "Default Group");
              }
              const groupedComponentArgs = componentArgs.reduce(
                (acc: Record<string, any[]>, arg: any) => {
                  const groupUuid = arg?.GroupUuid || "";
                  if (!acc[groupUuid]) acc[groupUuid] = [];
                  acc[groupUuid].push(arg);
                  return acc;
                },
                {}
              );
              const hasComponentConfigItems = [
                component.Role === "weather-monitor" ||
                  component.Role === "ping-monitor",
                component.Role === "inbound-api",
                component.Role === "inbound-message",
              ].some(Boolean);
              const isComponentDisabled =
                componentArgs.length === 0 && !hasComponentConfigItems;

              const orderedGroupKeys = [
                ...componentVariableGroups.map((group: any) => group.GroupUuid),
                ...Object.keys(groupedComponentArgs).filter(
                  (groupUuid) =>
                    groupUuid !== "" && !groupLabelByUuid.has(groupUuid)
                ),
              ];

              const renderComponentArg = (arg: any) => (
                <div
                  key={`${component.ComponentUuid}:${arg.UUID || arg.id}`}
                  className="rounded px-2 py-1 w-100 text-center btn btn-light"
                  style={{ cursor: "grab" }}
                  onPointerDown={(e) =>
                    onSidebarArgPointerDown(e, arg, component.ComponentUuid)
                  }
                >
                  <div>{arg.Label}</div>
                  <div className="text-secondary text-small">
                    {arg.Description}
                  </div>
                </div>
              );

              const isComponentConfigUsed = (arg: any) =>
                solution?.ComponentConfig?.steps?.some((step: any) =>
                  step.inputGroups.some((group: any) =>
                    group.inputs.some((input: any) => {
                      const sameUuid = input.UUID === arg.UUID;
                      const sameConfigIdentity =
                        input?.__kind === "component-config" &&
                        (input?.componentUuid === arg.componentUuid ||
                          input?.ComponentUuid === arg.componentUuid) &&
                        input?.configType === arg.configType;

                      return sameUuid || sameConfigIdentity;
                    })
                  )
                );

              // if (!componentArgs.length) return null;
              return (
                <div
                  key={component.ComponentUuid}
                  className={`border rounded p-2 mb-2 d-flex flex-column justify-content-center align-items-center gap-1 ${
                    isComponentDisabled ? "opacity-50" : ""
                  }`}
                >
                  {isComponentDisabled ? (
                    <div className="w-100">
                      <div className="d-flex align-items-center">
                        <div className="flex-grow-1 overflow-hidden text-navy fw-bold text-uppercase text-small text-nowrap">
                          {component.Item.Label}
                        </div>
                        <span className="p-1 px-2 ms-2 rounded-2 text-purple d-inline-block">
                          <i className="fa fa-angle-down" />
                        </span>
                      </div>
                      <div className="text-secondary text-small mt-2">
                        No variables available
                      </div>
                    </div>
                  ) : (
                    <MyCollapse
                      heading={
                        <div className="flex-grow-1 overflow-hidden text-navy fw-bold text-uppercase text-small text-nowrap">
                          {component.Item.Label}
                        </div>
                      }
                    >
                      {orderedGroupKeys.map((groupUuid: string) => {
                        const argsInGroup = [
                          ...(groupedComponentArgs[groupUuid] || []),
                        ].sort(
                          (a: any, b: any) => (a?.Order ?? 0) - (b?.Order ?? 0)
                        );

                        const groupLabel =
                          groupLabelByUuid.get(groupUuid) || "Other Variables";

                        return (
                          <div
                            key={`${component.ComponentUuid}:${groupUuid}`}
                            className="w-100 mb-2 bg-purple-gradient rounded p-1"
                          >
                            {argsInGroup.length === 0 ? (
                              <div className="w-100 opacity-50">
                                <div className="text-navy fw-bold text-uppercase text-small text-nowrap">
                                  {groupLabel}
                                </div>
                                <div className="text-secondary text-small mt-2">
                                  No variables available
                                </div>
                              </div>
                            ) : (
                              <MyCollapse
                                heading={
                                  <div className="text-navy fw-bold text-uppercase text-small text-nowrap">
                                    {groupLabel}
                                  </div>
                                }
                              >
                                <div className="d-flex flex-column gap-1">
                                  {argsInGroup.map((arg: any) =>
                                    renderComponentArg(arg)
                                  )}
                                </div>
                              </MyCollapse>
                            )}
                          </div>
                        );
                      })}

                      {(componentArgs.length > 0 ||
                        component.Role === "weather-monitor" ||
                        component.Role === "ping-monitor" ||
                        component.Role === "inbound-api" ||
                        component.Role === "inbound-message") && (
                        <div className="text-navy fw-bold text-uppercase text-small text-nowrap mt-2 mb-1">
                          Component Configuration
                        </div>
                      )}

                      {/* Component-config draggable items */}
                      {(component.Role === "weather-monitor" ||
                        component.Role === "ping-monitor") &&
                        (() => {
                          const arg = {
                            __kind: "component-config",
                            configType: component.Role,
                            componentUuid: component.ComponentUuid,
                            UUID: `component-config:${component.ComponentUuid}:monitor`,
                            Label: "Monitor Config",
                            Description: component.Item?.Label,
                          };
                          const isUsed = isComponentConfigUsed(arg);
                          if (isUsed) return null;
                          return (
                            <div
                              key={arg.UUID}
                              className="rounded px-2 py-1 w-100 text-center btn btn-light"
                              style={{ cursor: "grab" }}
                              onPointerDown={(e) =>
                                onSidebarArgPointerDown(
                                  e,
                                  arg,
                                  component.ComponentUuid
                                )
                              }
                            >
                              <div>{arg.Label}</div>
                              <div className="text-secondary text-small">
                                {arg.Description}
                              </div>
                            </div>
                          );
                        })()}

                      {component.Role === "inbound-api" &&
                        (() => {
                          const arg = {
                            __kind: "component-config",
                            configType: "inbound-api",
                            componentUuid: component.ComponentUuid,
                            UUID: `component-config:${component.ComponentUuid}:inbound-api`,
                            Label: "API Connection",
                            Description: component.Item?.Label,
                          };
                          const isUsed = isComponentConfigUsed(arg);
                          if (isUsed) return null;
                          return (
                            <div
                              key={arg.UUID}
                              className="rounded px-2 py-1 w-100 text-center btn btn-light"
                              style={{ cursor: "grab" }}
                              onPointerDown={(e) =>
                                onSidebarArgPointerDown(
                                  e,
                                  arg,
                                  component.ComponentUuid
                                )
                              }
                            >
                              <div>{arg.Label}</div>
                              <div className="text-secondary text-small">
                                {arg.Description}
                              </div>
                            </div>
                          );
                        })()}

                      {component.Role === "inbound-message" &&
                        (() => {
                          const arg = {
                            __kind: "component-config",
                            configType: "inbound-message",
                            componentUuid: component.ComponentUuid,
                            UUID: `component-config:${component.ComponentUuid}:inbound-message`,
                            Label: "Inbound Channel",
                            Description: component.Item?.Label,
                          };
                          const isUsed = isComponentConfigUsed(arg);
                          if (isUsed) return null;
                          return (
                            <div
                              key={arg.UUID}
                              className="rounded px-2 py-1 w-100 text-center btn btn-light"
                              style={{ cursor: "grab" }}
                              onPointerDown={(e) =>
                                onSidebarArgPointerDown(
                                  e,
                                  arg,
                                  component.ComponentUuid
                                )
                              }
                            >
                              <div>{arg.Label}</div>
                              <div className="text-secondary text-small">
                                {arg.Description}
                              </div>
                            </div>
                          );
                        })()}
                    </MyCollapse>
                  )}
                </div>
              );
            })}

            {Object.entries(importedJSON?.Bag?.External || {}).map(
              ([fieldType, fields]: [string, any]) => {
                if (!shouldShowExternalInputType(fieldType)) return null;

                const fieldEntries = Object.entries(fields || {});
                if (!fieldEntries.length) return null;

                return (
                  <div
                    key={fieldType}
                    className="border rounded p-2 mb-2 d-flex flex-column align-items-center gap-1"
                  >
                    <MyCollapse
                      heading={
                        <div className="flex-grow-1 overflow-hidden text-navy fw-bold text-uppercase text-small text-nowrap">
                          External {fieldType}
                        </div>
                      }
                    >
                      {fieldEntries
                        .map(([fieldKey, fieldValue]: [string, any]) =>
                          toExternalDragArg(fieldType, fieldKey, fieldValue)
                        )
                        .filter((arg) => {
                          const isUsed = solution?.ComponentConfig?.steps?.some(
                            (step: any) =>
                              step.inputGroups.some((group: any) =>
                                group.inputs.some((input: any) => {
                                  const directMatch = input.UUID === arg.UUID;
                                  const mergedRefMatch =
                                    Array.isArray(input.MergedInputRefs) &&
                                    input.MergedInputRefs.some(
                                      (ref: any) =>
                                        (ref?.__kind === "external" &&
                                          ref?.fieldType === arg.fieldType &&
                                          ref?.fieldKey === arg.fieldKey) ||
                                        (ref?.ComponentUuid ===
                                          `external:${arg.fieldType}` &&
                                          ref?.Field === arg.fieldKey)
                                    );

                                  return directMatch || mergedRefMatch;
                                })
                              )
                          );
                          return !isUsed;
                        })
                        .map((arg) => (
                          <div
                            key={arg.UUID}
                            className="rounded px-2 py-1 w-100 text-center btn btn-light"
                            style={{ cursor: "grab" }}
                            onPointerDown={(e) =>
                              onSidebarArgPointerDown(
                                e,
                                arg
                                // component.ComponentUuid
                              )
                            }
                          >
                            <div>{arg.Label}</div>
                            <div className="text-secondary text-small">
                              {arg.Description}
                            </div>
                          </div>
                        ))}
                    </MyCollapse>
                  </div>
                );
              }
            )}
          </div>
        )}
        <div
          className="col border-end overflow-auto"
          ref={setupFormContainerRef}
          style={{ maxHeight: "100%" }}
        >
          {isAdminMode && (
            <MyModal
              as="offcanvas"
              show={isAiAssistantSidebarOpen}
              onHide={() => setIsAiAssistantSidebarOpen(false)}
              label={null}
              buttonClassName="d-none"
              placement="start"
              heading={
                <span>Setup improvements</span>
              }
              style={{ width: "420px", maxWidth: "100%" }}
            >
            <div className="d-flex flex-column gap-2">
              <div className="d-flex align-items-center gap-2 flex-wrap">
                <button
                  type="button"
                  className="btn btn-secondary btn-sm d-flex align-items-center gap-1 text-white"
                  onClick={() => requestAiAssistantSuggestions("full")}
                  disabled={isAiAssistantLoading}
                >
                  {isAiAssistantLoading && (
                    <span
                      className="spinner-border spinner-border-sm"
                      role="status"
                      aria-hidden="true"
                    />
                  )}
                  <span>Analyze solution setup</span>
                </button>
                {aiAssistantSuggestions.length > 0 && (
                  <button
                    type="button"
                    className="btn btn-outline-secondary btn-sm"
                    onClick={() => {
                      setAiAssistantSuggestions([]);
                      setAiAssistantError("");
                    }}
                    disabled={isAiAssistantLoading}
                  >
                    Remove all
                  </button>
                )}
              </div>
              {isAiAssistantLoading && (
                <div className="d-flex align-items-center gap-2 text-secondary text-small">
                  <span
                    className="spinner-border spinner-border-sm"
                    role="status"
                    aria-hidden="true"
                  />
                  <span>Checking setup improvements...</span>
                </div>
              )}
              {!isAiAssistantLoading && aiAssistantError && (
                <div className="text-secondary text-small">
                  {aiAssistantError}
                </div>
              )}
              {aiAssistantSuggestions.length > 0 && (
                <div className="d-flex flex-column gap-2">
                  {aiAssistantSuggestions.map((suggestion, index) => {
                    const suggestedChange = suggestion.action?.change;
                    const currentText = stringifySetupValue(
                      suggestedChange?.from
                    );
                    const suggestionText = stringifySetupValue(
                      suggestedChange?.to
                    );

                    return (
                      <div
                        key={`${suggestion.title}-${index}`}
                        className="bg-white rounded border p-2"
                        role="button"
                        tabIndex={0}
                        style={{ cursor: "pointer" }}
                        onClick={() =>
                          handleAiAssistantSuggestionClick(suggestion)
                        }
                        onKeyDown={(event) => {
                          if (event.key === "Enter" || event.key === " ") {
                            event.preventDefault();
                            handleAiAssistantSuggestionClick(suggestion);
                          }
                        }}
                      >
                        <div className="d-flex align-items-start justify-content-between gap-2">
                          <div>
                            {suggestion.stage && (
                              <div className="text-secondary text-small">
                                {suggestion.stage}
                              </div>
                            )}
                            {!currentText || !suggestionText ? (
                              <div className="fw-bold text-small">
                                {suggestion.title}
                              </div>
                            ) : null}
                          </div>
                          <div className="d-flex align-items-center gap-1">
                            <button
                              type="button"
                              className="btn btn-outline-secondary btn-sm"
                              onClick={(event) => {
                                event.stopPropagation();
                                setAiAssistantSuggestions((prevSuggestions) =>
                                  prevSuggestions.filter(
                                    (item) => item !== suggestion
                                  )
                                );
                              }}
                              disabled={isAiAssistantLoading}
                            >
                              Remove
                            </button>
                            <button
                              type="button"
                              className="btn btn-outline-primary btn-sm"
                              onClick={(event) => {
                                event.stopPropagation();
                                handleApplyAiAssistantSuggestion(suggestion)
                              }}
                              disabled={isAiAssistantLoading}
                            >
                              Apply
                            </button>
                          </div>
                        </div>
                        {currentText && suggestionText ? (
                          <div className="d-flex flex-column gap-1 mt-1">
                            <div>
                              <div className="fw-bold text-small">
                                {currentText}
                              </div>
                            </div>
                            <div className="text-secondary text-small">=&gt;</div>
                            <div>
                              <div className="fw-bold text-small">
                                {suggestionText}
                              </div>
                            </div>
                          </div>
                        ) : null}
                        {suggestion.detail && (
                          <div className="text-secondary text-small mt-1">
                            {suggestion.detail}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
            </MyModal>
          )}
          <Tab.Container id="solution-steps-tabs" activeKey={activeStepIndex}>
            <Tab.Content data-setup-steps-content>
              {visibleSteps.map(
                ({ step, originalIndex }: any, visibleIndex: number) => {
                  const stepKey =
                    step.inputGroups?.[0]?.id ||
                    `${step.name}-${step.description}-${originalIndex}`;
                  return (
                    <Tab.Pane key={stepKey} eventKey={originalIndex}>
                      <SolutionStep
                        step={step}
                        stepIndex={originalIndex}
                        totalSteps={
                          visibleSteps.length || 0
                        }
                        focusedInputNodeKey={focusedInputNodeKey}
                        onPreviousStep={() =>
                          focusStep(
                            visibleSteps[
                              Math.max(0, visibleIndex - 1)
                            ]?.originalIndex ?? originalIndex
                          )
                        }
                        onNextStep={() =>
                          focusStep(
                            visibleSteps[
                              Math.min(
                                visibleSteps.length - 1,
                                visibleIndex + 1
                              )
                            ]?.originalIndex ?? originalIndex
                          )
                        }
                        sidebarDragging={!!sidebarDragState}
                        sidebarOverInput={sidebarOverInput}
                        setStep={
                          setSolution
                            ? (updatedStep: any) => {
                                setSolution({
                                  ...solution,
                                  ComponentConfig: {
                                    ...solution.ComponentConfig,
                                    steps: solution.ComponentConfig.steps.map(
                                      (s: any, i: number) =>
                                        i === originalIndex ? updatedStep : s
                                    ),
                                  },
                                });
                              }
                            : () => {}
                        }
                        onDelete={
                          setSolution
                            ? () => {
                                setSolution({
                                  ...solution,
                                  ComponentConfig: {
                                    ...solution.ComponentConfig,
                                    steps:
                                      solution.ComponentConfig.steps.filter(
                                        (_: any, i: number) =>
                                          i !== originalIndex
                                      ),
                                  },
                                });
                                const nextStepIndex = Math.max(
                                  0,
                                  activeStepIndex > originalIndex
                                    ? activeStepIndex - 1
                                    : Math.min(
                                        activeStepIndex,
                                        solution.ComponentConfig.steps.length -
                                          2
                                      )
                                );
                                focusStep(nextStepIndex);
                              }
                            : undefined
                        }
                        formData={formData}
                        setFormData={setFormData}
                        showDefaultValueInputsOnly={showDefaultValueInputsOnly}
                        importContext={{
                          importedJSON,
                          setImportedJSON,
                          manualFields,
                          setManualFields,
                          automaticallySelectedFields,
                          setAutomaticallySelectedFields,
                          contactSets: contactSetsData?.contactSets || [],
                          reportingTopics: reportingTopicsData?.data || [],
                          apiDataCollections:
                            apiDataCollectionsData?.collections || [],
                          apiProfiles,
                          setApiProfiles,
                          updateApiConnection: updateApiConnection as any,
                          monitors,
                          setMonitors,
                          inboundAPIs,
                          setInboundAPIs,
                          inboundCampaigns,
                          setInboundCampaigns,
                          disableInputEditing,
                          disableInputDragging,
                          disableStructureEditing: isUserMode,
                          showDefaultValueInputsOnly,
                        }}
                        onInputFocus={(inputGroupId: string, input?: any) => {
                          setFocusedStepIndex(originalIndex);
                          setFocusedInputGroupId(
                            `step:${originalIndex}|group:${inputGroupId}`
                          );
                          setFocusedInputNodeKey(
                            getInputFocusKey(originalIndex, inputGroupId, input)
                          );
                        }}
                        onInputBlur={(inputGroupId?: string, input?: any) => {
                          const blurKey = inputGroupId
                            ? getInputFocusKey(
                                originalIndex,
                                inputGroupId,
                                input
                              )
                            : null;
                          setFocusedInputNodeKey((prev) =>
                            prev && blurKey && prev !== blurKey ? prev : null
                          );
                          setFocusedInputGroupId((prev) =>
                            prev &&
                            inputGroupId &&
                            prev !==
                              `step:${originalIndex}|group:${inputGroupId}`
                              ? prev
                              : null
                          );
                        }}
                        usedInputs={usedInputs}
                        inputUsageByKey={inputUsageByKey}
                        onUsageSelect={handleUsageSelect}
                        getIsVisible={getIsVisible}
                        aiAssistantSuggestions={aiAssistantSuggestions}
                        isAiAssistantLoading={isAiAssistantLoading}
                        onAiAssistantSuggestionClick={
                          handleAiAssistantSuggestionClick
                        }
                        onApplyAiAssistantSuggestion={
                          handleApplyAiAssistantSuggestion
                        }
                        onRemoveAiAssistantSuggestion={(suggestion: any) =>
                          setAiAssistantSuggestions((prevSuggestions) =>
                            prevSuggestions.filter((item) => item !== suggestion)
                          )
                        }
                      />
                    </Tab.Pane>
                  );
                }
              )}
            </Tab.Content>
          </Tab.Container>
        </div>
        <div className="col-5 h-100 d-flex flex-column overflow-hidden">
          <MyModal
            heading={
              diagramInput?.Label ? `Edit ${diagramInput.Label}` : "Edit input"
            }
            label={null}
            buttonClassName="d-none"
            show={
              !!diagramInputModal &&
              !!diagramInputStep &&
              !!diagramInputGroup &&
              !!diagramInput
            }
            onHide={() => setDiagramInputModal(null)}
            size="lg"
          >
            {diagramInputModal && diagramInputStep && diagramInputGroup ? (
              <SolutionStepInputGroup
                inputGroup={diagramInputGroup}
                step={diagramInputStep}
                setStep={
                  setSolution && solution
                    ? (updatedStep: any) => {
                        setSolution({
                          ...solution,
                          ComponentConfig: {
                            ...solution.ComponentConfig,
                            steps: solution.ComponentConfig.steps.map(
                              (step: any, stepIndex: number) =>
                                stepIndex === diagramInputModal.stepIndex
                                  ? updatedStep
                                  : step
                            ),
                          },
                        });
                      }
                    : () => {}
                }
                disableInputEditing={disableInputEditing}
                disableInputDragging
                formData={formData}
                setFormData={setFormData}
                onGroupDragHandlePointerDown={() => {}}
                onStartInputDrag={() => {}}
                importContext={{
                  importedJSON,
                  setImportedJSON,
                  manualFields,
                  setManualFields,
                  contactSets: contactSetsData?.contactSets || [],
                  reportingTopics: reportingTopicsData?.data || [],
                  apiDataCollections: apiDataCollectionsData?.collections || [],
                  apiProfiles,
                  setApiProfiles,
                  updateApiConnection: updateApiConnection as any,
                  monitors,
                  setMonitors,
                  inboundAPIs,
                  setInboundAPIs,
                  inboundCampaigns,
                  setInboundCampaigns,
                  disableInputEditing,
                  disableInputDragging: true,
                  disableStructureEditing: isUserMode,
                  showDefaultValueInputsOnly,
                }}
                usedInputs={usedInputs}
                inputUsageByKey={inputUsageByKey}
                onUsageSelect={handleUsageSelect}
                getIsVisible={getIsVisible}
                inputIndexFilter={diagramInputModal.inputIndex}
                hideInputGroupHeader
                showDefaultValueInputsOnly={showDefaultValueInputsOnly}
              />
            ) : null}
          </MyModal>
          <Tab.Container
            activeKey={activeDiagramView}
            onSelect={(key) => {
              if (key === "solutionSteps" || key === "componentFlow") {
                setActiveDiagramView(key);
              }
            }}
          >
            <Nav variant="tabs" className="px-4 flex-shrink-0">
              <Nav.Item>
                <Nav.Link eventKey="solutionSteps">
                  Solution step diagram
                </Nav.Link>
              </Nav.Item>
              <Nav.Item>
                <Nav.Link eventKey="componentFlow">
                  Component flow diagram
                </Nav.Link>
              </Nav.Item>
            </Nav>
            <Tab.Content className="flex-grow-1 overflow-hidden">
              <Tab.Pane
                eventKey="solutionSteps"
                className="h-100 overflow-hidden"
                mountOnEnter
                unmountOnExit
              >
                {activeDiagramView === "solutionSteps" && (
                  <SolutionFlowOverview
                    solution={solution}
                    viewMode="solutionSteps"
                    activeStepIndex={focusedStepIndex ?? activeStepIndex}
                    focusedInputGroupId={focusedInputGroupId}
                    focusedInputNodeKey={focusedInputNodeKey}
                    focusedLogicNodeKey={null}
                    onNodeClick={handleFlowNodeClick}
                  />
                )}
              </Tab.Pane>
              <Tab.Pane
                eventKey="componentFlow"
                className="h-100 overflow-hidden"
                mountOnEnter
                unmountOnExit
              >
                {activeDiagramView === "componentFlow" && (
                  <SolutionFlowOverview
                    solution={solution}
                    viewMode="componentFlow"
                    activeStepIndex={focusedStepIndex ?? activeStepIndex}
                    focusedInputGroupId={null}
                    focusedInputNodeKey={null}
                    focusedLogicNodeKey={focusedLogicNodeKey}
                    onNodeClick={handleFlowNodeClick}
                  />
                )}
              </Tab.Pane>
            </Tab.Content>
          </Tab.Container>
        </div>
      </div>
    </div>
  );
};

export default SolutionSetup;
